<script lang="ts" setup>
// SourceDataEnedisView (DEV-1508 lot 2) — la vue « Données du compteur » d'un
// compteur Enedis, derrière l'interrupteur `ENEDIS_PAGE_DONNEES_V2` et servie
// aujourd'hui par l'adresse « ombre ».
//
// De haut en bas, ce que voit celui qui regarde son compteur :
//
//  1. LES HUIT FAITS du point, et la FRAÎCHEUR lue du verdict. Aucun seuil de
//     jours ne vit ici (E4) : c'est le juge qui sait ce que ce compteur doit.
//  2. LE SENS regardé, quand le compteur en porte deux. Miroir, jamais une
//     somme, jamais un net (E5).
//  3. LA BANDE de complétude du contexte, jour par jour, quatre mots. Les trous
//     se lisent À LEUR DATE, pas dans une bannière (décision du 08/09).
//  4. LES ONGLETS que les variables SERVIES décident, chargés au clic, aucun
//     vide, aucune série sur un nom que ce compteur ne sert pas (E1).
//  5. DANS UN ONGLET : les graphes, un par sens et par unité, chacun avec son
//     dépliant « Valeurs » (E2).
//
// La navigation : un cliquer-glisser sur un graphe OU sur la bande règle la
// fenêtre regardée, partagée par les graphes de l'onglet et conservée d'un
// onglet à l'autre ; on revient au contexte par double-clic ou par le bouton.
// Au clavier, les deux champs « Du » et « Au » de la barre font le même
// recadrage, par le même chemin (lot B5) : depuis ce lot, resserrer la fenêtre
// n'est plus réservé à la souris.
// Pas de bouton Zoomer, pas de fenêtre préréglée, pas de grain manuel.
//
// La fenêtre regardée EST l'axe des temps de TOUS les graphes de l'onglet :
// elle descend en `fenetre` à chaque graphe, qui la pose sur son axe. Resserrer
// resserre l'axe ; revenir au contexte rend l'axe du contexte.
//
// Rien de neuf ailleurs (E10) : aucune bannière, aucun export nouveau.
import { computed, onMounted, onUnmounted, ref, watch } from "vue"
import type {
    InformationPDL,
    VariableDataV2Servie,
    WorkflowEnedisVerdict,
} from "@types"
import type {
    DirectionEnedis,
    GrainSeries,
    ReponseSeries,
} from "@modules/data/services/sourceDataEnedis.service"
import {
    enteteDuCompteur,
    etatServiDuCompteur,
    faitsConnus,
    titreDuCompteur,
} from "@modules/data/utils/enteteEnedis"
import {
    classeDuPoint,
    dateFr,
    fraicheurAPeindre,
    ligneDeFraicheurDeLaFamille,
    derniereValeurDeLaFamille as derniereValeurDeLaFamilleServie,
} from "@modules/data/utils/fraicheurEnedis"
import type { ContexteDesFlux } from "@modules/data/utils/fluxEnedis"
import {
    dernierJourDuDuFlux,
    fluxDeLaFamille,
} from "@modules/data/utils/fluxEnedis"
import {
    DIRECTIONS,
    LIBELLES_DIRECTION,
    ongletsParSegment,
    variablesDeLOnglet,
    type CleOnglet,
    type OngletResolu,
    type SegmentEnedis,
} from "@modules/data/utils/ongletsEnedis"
import {
    identiteDeLaCarte,
    pasLePlusProche,
    pasPermis,
    raisonDuRepli,
    seriesDeLaCarte,
} from "@modules/data/utils/pasDeLaCarte"
import {
    energieEnSemaines,
    axeYAuxValeurs,
    dernierPointFini,
    estSommable,
    estSommableSurLaFenetre,
    estVideSurLaFenetre,
    graphesDeLOnglet,
    joursMesuresDeLaBande,
    joursMesuresDesReponses,
    renduDeLOnglet,
    SEUIL_SEMAINE_JOURS,
    variableDeLaBande,
    type GrapheEnedis,
    type SerieTracee,
} from "@modules/data/utils/graphesEnedis"
import {
    fenetreDeLaBande,
    fenetreDuBrush,
    fenetreTracee,
    memeFenetre,
    type GesteRecadrage,
} from "@modules/data/utils/navigationEnedis"
import {
    cleColonneAlignee,
    grainDuTableau,
    minutesDuPas,
    type GrainTableau,
} from "@modules/data/utils/tableauValeurs"
import {
    couvre,
    grainRetenu,
    joursDeFenetre,
    niveauPourFenetre,
    useSeriesEnedis,
    SEUIL_HORAIRE_JOURS,
    SEUIL_NATIF_JOURS,
    type Fenetre,
    type LecteurSeries,
    type NiveauSeries,
    type ResultatChargement,
} from "@modules/data/composables/useSeriesEnedis"
import {
    JOURS_DU_CONTEXTE,
    RACCOURCIS_DE_CONTEXTE,
    jourDeLecture,
} from "@modules/data/utils/pageCompteurV2"
import { creerDetecteurFriction } from "@modules/data/utils/frictionEnedis"
import { demarrerReplaySurLaRoute } from "@modules/data/utils/replayCompteur"
import { track } from "@core/tracking/track"
import {
    completudeDeJoursMesures,
    plagesDuStatut,
    plagesDuVerdict,
    texteDuRappel,
    texteDuRappelDesDepassements,
    PLAFOND_JOURS,
    plagesDuSens,
    type Completude,
} from "@modules/data/utils/completude"
import { useResponsive } from "@core/composables/useResponsive"
import DataCompletudeBar from "@modules/data/components/data/completude/DataCompletudeBar.vue"
// Import EXPLICITE, et non l'auto-import de l'application : `vitest.config.ts`
// ne charge que le plugin `vue()`, donc un composant seulement auto-importé
// serait rendu comme élément inconnu et ses slots nommés ne seraient jamais
// rendus. Voir `monterEnedis.spec.helper.ts`.
import PlotlyChartCard from "@modules/data/components/data/chart/PlotlyChartCard.vue"
import GraphiqueSeriesEnedis from "./GraphiqueSeriesEnedis.vue"
import TableauValeursEnedis from "./TableauValeursEnedis.vue"
import type { EntreePageCompteur } from "@modules/data/config/enedis-page-donnees-v2.flag"

/** Le filtre de sens, tel qu'il se choisit à l'écran. */
type FiltreDirection = "les_deux" | DirectionEnedis

const props = withDefaults(
    defineProps<{
        /** Identifiant de la source. Sans lui, rien n'est chargé ni tracé. */
        idSource?: string | null
        /** Numéro du point, déjà formaté par l'écran appelant. */
        prm?: string | null
        informations?: InformationPDL | null
        /** Le verdict déjà chargé : la fraîcheur s'y lit, elle ne se calcule pas. */
        verdict?: WorkflowEnedisVerdict | null
        /**
         * Vrai quand la lecture du verdict est TERMINÉE, qu'elle ait rendu un
         * verdict ou non. Distinct de `verdict` : `null` veut dire « pas
         * encore lu » aussi bien que « rien à dire », et la bande de complétude
         * ne peut pas se peindre sur cette ambiguïté (décision du 11/09).
         */
        verdictRecu?: boolean
        segment?: SegmentEnedis
        /** Les sens que ce compteur porte réellement. */
        directions?: DirectionEnedis[]
        /**
         * Les variables réellement servies par ce compteur, PAR SENS. Ce sont
         * elles, et elles seules, qui décident des onglets et des séries (E1) :
         * sans elles, la page n'ouvre aucun onglet plutôt que d'en inventer.
         */
        variablesServies?: Partial<
            Record<DirectionEnedis, VariableDataV2Servie[]>
        > | null
        /** La période chargée, plafonnée par le dû. Elle borne tout. */
        contexte?: Fenetre | null
        /**
         * La LARGEUR du contexte demandée, en jours : celle du raccourci pressé.
         *
         * Elle descend de l'écran appelant parce que c'est lui qui calcule le
         * contexte (`contexteDeLaPageCompteur`, une seule fonction pour les deux
         * entrées). La vue ne recalcule donc aucune date : elle dit quel
         * raccourci est pressé, et demande le suivant.
         */
        joursContexte?: number
        entree?: EntreePageCompteur
        /** Le lecteur de séries. Injectable : un double dans les tests. */
        lecteur?: LecteurSeries
        /**
         * LE JOUR DE LECTURE, en ISO court. Injectable pour les récits.
         *
         * Il sert UNIQUEMENT à confronter un verdict servi à ses propres dates
         * (règle du lot 5h : un « à jour » figé au 22/08 ne peut plus être vrai
         * le 17/09). Sans lui, c'est aujourd'hui. Les récits l'épinglent, sinon
         * ils virent au rouge le lendemain de leur écriture, et ce n'est pas une
         * régression du code qu'ils signaleraient alors.
         */
        aujourdHui?: string
    }>(),
    {
        idSource: null,
        prm: null,
        informations: null,
        verdict: null,
        verdictRecu: false,
        segment: "INDET",
        directions: () => ["soutirage"],
        variablesServies: null,
        contexte: null,
        joursContexte: JOURS_DU_CONTEXTE,
        entree: "standard",
        lecteur: undefined,
        aujourdHui: undefined,
    }
)

const emit = defineEmits<{
    /** Un raccourci de contexte a été pressé : l'écran appelant recalcule. */
    (e: "update:joursContexte", jours: number): void
}>()

const { charger, enCours } = useSeriesEnedis(props.lecteur)

/**
 * Les deux détecteurs de friction. Un événement par RAFALE, jamais par clic, et
 * rien d'autre qu'un type, une cible et un compte (contrat §3.1).
 */
const detecteur = creerDetecteurFriction((friction) => {
    if (!props.idSource) return
    track({
        event: "Compteur Friction Détectée",
        payload: { source_id: props.idSource, ...friction },
    })
})

// ============================================================================
// L'EN-TÊTE
// ============================================================================

const faits = computed(() =>
    faitsConnus(
        enteteDuCompteur({
            prm: props.prm,
            informations: props.informations,
            directions: props.directions,
        })
    )
)

/**
 * LE STATUT AGRÉGÉ DE COLLECTE, déjà servi par `/source/informations` avec le
 * reste de l'en-tête. Aucun appel de plus ne part pour le lire.
 */
const statut = computed(
    () => props.informations?.data_retrieval?.status ?? null
)

/**
 * LA FRAÎCHEUR : le verdict PRIME, le statut supplée (lot 5a, écart B2).
 *
 * Le verdict dit le détail PAR FLUX, ce que le statut ignore : quand il est là,
 * il gagne, sans condition. Mais il n'est chargé que pour les workflows
 * `state=run` : sur un compteur sain il n'existe pas, et le bloc entier ne se
 * peignait JAMAIS (aucun `[data-testid="fraicheur"]` sur les trois compteurs
 * témoins en prod le 15/09). Le statut, lui, est servi pour tout compteur
 * instruit. Les deux `null` = rien n'est écrit, et c'est encore la bonne réponse.
 */
/**
 * CE QU'IL FAUT POUR NOMMER LES FLUX DUS (DEV-1845 tranche 2) : le segment, qui
 * décide du grain de la PMax, le pas de la courbe PAR SENS, et le fait que le
 * compteur porte les deux sens — auquel cas chaque ligne nomme le sien.
 */
const contexteDesFlux = computed<ContexteDesFlux>(() => ({
    segment: props.informations?.compteur?.segment ?? null,
    pasDeLaCourbe: {
        soutirage: props.informations?.data?.soutirage?.cdc?.pas ?? null,
        injection: props.informations?.data?.injection?.cdc?.pas ?? null,
    },
    deuxSens: props.directions.length > 1,
}))

/**
 * LE DÉTAIL PAR FLUX PRIME SUR LE VERDICT (DEV-1845 tranche 2).
 *
 * L'ordre du lot 5a était « le verdict d'abord, le statut en repli », parce que
 * le verdict était la seule source qui connaissait les flux. `status.flux` les
 * connaît TOUS, frais compris, avec la PROMESSE de chacun — le verdict ne liste
 * que les retards et ne porte que le seuil d'alerte. Quand il est servi, il
 * gagne ; sinon l'ordre d'avant reprend, au mot près.
 *
 * Le choix vit dans `fraicheurAPeindre` (27/10) : la collecte INTERROMPUE y fait
 * aussi primer le statut, même avec `flux: []`.
 */
const fraicheurServie = computed(() =>
    fraicheurAPeindre({
        statut: statut.value,
        verdict: props.verdict,
        aujourdHui: props.aujourdHui,
        contexte: contexteDesFlux.value,
    })
)

/**
 * L'ÉTAT SERVI PAR ENEDIS PRIME, ET FAIT TAIRE LA FRAÎCHEUR (lot 5b, §7.7).
 *
 * `PageSourceData` sert déjà une cause pour un compteur inexistant (« Enedis
 * nous a remonté que le compteur … n'existe pas sur le réseau »), et un
 * compteur résilié ne reçoit plus de mesures par construction. Sur ces deux
 * points, le statut de collecte dirait pourtant « En retard de n j · donnée non
 * livrée » : deux états contradictoires sur le même écran, exactement le défaut
 * corrigé en DEV-1619. Le TITRE porte alors l'état servi, et le bloc de
 * fraîcheur ne se peint pas.
 */
const etatServi = computed(() => etatServiDuCompteur(props.informations))

const fraicheur = computed(() =>
    etatServi.value ? null : fraicheurServie.value
)

/**
 * LE TITRE DE LA CARTE (écart E1) : « Compteur C5 (LINKY), à jour ». Il ne
 * calcule rien : il recopie le segment, le modèle et ce que la fraîcheur a
 * conclu.
 */
const titreDeLaCarte = computed(() =>
    titreDuCompteur({
        informations: props.informations,
        fraicheur: fraicheur.value,
    })
)

// ============================================================================
// LE SENS REGARDÉ
// ============================================================================

/**
 * LE SÉLECTEUR N'EXISTE QUE SI LES DEUX SENS SONT RÉELLEMENT SERVIS (lot 5g).
 *
 * Le fait est le même que celui qui décide des onglets : `variables_datav2` non
 * vide de CHAQUE côté. Un sens déclaré par le référentiel mais sans une seule
 * variable servie n'a rien à montrer, et lui offrir une position du sélecteur
 * mène le client sur une page vide — c'est ce que servait le P1 pur
 * `666069190b1d7fc6def335b4` en prod le 17/09.
 *
 * `props.directions` est déjà filtré par `directionsDuPdl` ; la seconde
 * condition garde la vue contre un appelant qui passerait un sens de plus.
 */
/**
 * Une demande PAR SENS, pour le composable (lot 5g). Le sens qui n'a rien à
 * demander n'a pas d'entrée : il ne déclenche aucun appel.
 */
function parSens<T>(
    directions: DirectionEnedis[],
    pour: (direction: DirectionEnedis) => T[]
): Partial<Record<DirectionEnedis, T[]>> {
    const demande: Partial<Record<DirectionEnedis, T[]>> = {}
    for (const direction of directions) demande[direction] = pour(direction)
    return demande
}

const lesDeuxSens = computed(
    () =>
        props.directions.length > 1 &&
        props.directions.every(
            (d) => (props.variablesServies?.[d]?.length ?? 0) > 0
        )
)
const filtre = ref<FiltreDirection>("les_deux")

/** Les sens réellement regardés. « Les deux » = deux jeux, jamais une somme. */
const directionsRegardees = computed<DirectionEnedis[]>(() =>
    filtre.value === "les_deux"
        ? props.directions
        : props.directions.filter((d) => d === filtre.value)
)

/** Les choix offerts : rien à choisir sur un compteur qui n'a qu'un sens. */
const choixDirection = computed<
    Array<{ cle: FiltreDirection; libelle: string }>
>(() =>
    lesDeuxSens.value
        ? [
              { cle: "les_deux", libelle: "Les deux" },
              ...props.directions.map((d) => ({
                  cle: d as FiltreDirection,
                  libelle: LIBELLES_DIRECTION[d],
              })),
          ]
        : []
)

// ============================================================================
// LES ONGLETS
// ============================================================================

const onglets = computed(() =>
    ongletsParSegment({
        directions: props.directions,
        servies: props.variablesServies,
    })
)

const ongletActif = ref<CleOnglet | null>(onglets.value[0]?.cle ?? null)
const ouverts = ref<Set<CleOnglet>>(
    new Set(ongletActif.value ? [ongletActif.value] : [])
)

watch(onglets, (liste) => {
    if (!liste.some((o) => o.cle === ongletActif.value)) {
        ongletActif.value = liste[0]?.cle ?? null
        ouverts.value = new Set(ongletActif.value ? [ongletActif.value] : [])
    }
})

const ongletCourant = computed<OngletResolu | null>(
    () => onglets.value.find((o) => o.cle === ongletActif.value) ?? null
)

// ============================================================================
// LE FOCUS ET LES DONNÉES
// ============================================================================

/** La fenêtre regardée. Conservée d'un onglet à l'autre (E6). */
const focus = ref<Fenetre | null>(props.contexte)
watch(
    () => props.contexte,
    (contexte) => {
        if (!focus.value) focus.value = contexte
    }
)

const auContexte = computed(() => memeFenetre(focus.value, props.contexte))

/**
 * La fenêtre regardée, écrite en dates lisibles, avec SA LARGEUR (écart T8) :
 * « du 13/06/2026 au 10/09/2026 · 90 j ».
 *
 * Le nombre de jours est ce que la maquette met en avant, et c'est lui qui
 * explique le niveau servi : « 92 j » et « enveloppe horaire (15 à 92 j) » se
 * lisent ensemble. Sans lui, il faut compter sur ses doigts entre deux dates.
 */
const fenetreLisible = computed<string | null>(() => {
    const f = focus.value
    if (!f) return null
    const debut = dateFr(f.debut)
    const fin = dateFr(f.fin)
    if (!debut || !fin) return null
    return `du ${debut} au ${fin} · ${joursDeFenetre(f)} j`
})

/** Le contexte, écrit comme la maquette : « 09/06/2026 → 08/09/2026 ». */
const contexteLisible = computed<string | null>(() => {
    const c = props.contexte
    if (!c) return null
    const debut = dateFr(c.debut)
    const fin = dateFr(c.fin)
    if (!debut || !fin) return null
    return `${debut} → ${fin}`
})

// ============================================================================
// LA BANDE DE COMPLÉTUDE (E3)
// ============================================================================

/**
 * La complétude du CONTEXTE, calculée ICI et nulle part ailleurs.
 *
 * Elle acceptait, jusqu'au lot 3c, une complétude servie par l'écran appelant —
 * et cette porte n'était pas gardée : la page de données standard y passait la
 * sienne, calculée sur les lignes de ses indicateurs, sur sa période et sur les
 * variables de ses graphiques. Un autre jeu de séries, un autre référentiel de
 * jours, d'où le « 0 / 93 jours mesurés (0 %) » servi le 11/09 sur staging à
 * côté d'un graphe qui montrait des mesures. Il n'y a plus qu'un calcul, donc
 * plus qu'une réponse : les deux entrées lisent les mêmes mots.
 *
 * Sans cette bande, le trou du 28/06 ne se verrait NULLE PART : le graphe
 * s'arrêterait, sans un mot, ce qui est exactement ce que la décision du 08/09
 * interdit (un trou se représente DANS la donnée).
 *
 * Elle se déduit de ce que l'onglet vient de charger, sans une requête de plus :
 * les jours portant un point fini sont mesurés, les autres prennent l'état que
 * le VERDICT donne à leur date (jamais un seuil du front, E4). On ne la retient
 * que d'un chargement qui couvre le contexte ENTIER : une fenêtre resserrée
 * dirait « manquant » sur des jours simplement pas demandés.
 */
/**
 * ⚠️ Ce qui est retenu ici, ce sont les JOURS MESURÉS, pas la complétude.
 *
 * Le lot 2e retenait la complétude déjà calculée, verdict compris. Les deux
 * faits n'arrivent pourtant pas dans un ordre garanti : selon que le verdict
 * précédait ou suivait le premier chargement, le MÊME écran, avec le MÊME
 * verdict, écrivait « Manquant 75 » ou « Collecte 75 » — et le gel de la
 * peinture jusqu'au verdict (lot 2e) ne changeait rien, puisque c'est
 * l'instantané, pris trop tôt, qui était faux. Un changement d'onglet
 * recalculait l'instantané, cette fois avec le verdict : d'où le même écran qui
 * change de mot en cours de route.
 *
 * Les jours mesurés, eux, ne dépendent que des séries chargées. Le verdict
 * s'applique ensuite, à chaque lecture : mêmes jours et même verdict donnent
 * toujours les mêmes mots, et l'arrivée tardive du verdict RECALCULE la bande
 * au lieu de la figer.
 */
/**
 * LES JOURS MESURÉS DU CONTEXTE, PAR SENS (lot 5g).
 *
 * Un compteur à deux sens a deux complétudes, et elles diffèrent : sur le C4
 * témoin de la maquette, le soutirage est à jour au 08/09 quand l'injection
 * s'arrête au 04/09. Les fondre en un seul compte ferait passer les quatre
 * jours manquants de l'injection pour des jours mesurés.
 */
const joursMesuresParSens = ref<Partial<Record<DirectionEnedis, string[]>>>({})

/**
 * LA BANDE NE SE PEINT QU'UNE FOIS LE VERDICT ARRIVÉ (décision du 11/09).
 *
 * Sans le verdict, tout jour sans point tombe en « Manquant » : la bande
 * annonçait « Manquant 74 », puis se reniait en « Collecte 74 » une seconde
 * plus tard, quand le verdict arrivait enfin. Un mot d'état affiché puis démenti
 * apprend au client quelque chose de faux ; on préfère ne rien dire encore.
 *
 * Ce qu'on met à la place n'est PAS une bannière ni un message d'attente : c'est
 * l'emplacement de la bande, tenu, muet, le temps que le verdict arrive.
 *
 * La complétude SERVIE par l'écran appelant n'est pas concernée : elle est déjà
 * arbitrée par lui, elle ne changera pas sous les yeux du client.
 */
function completudeDuSens(direction: DirectionEnedis): Completude | null {
    const contexte = props.contexte
    const jours = joursMesuresParSens.value[direction]
    if (!props.verdictRecu || !contexte || !jours) return null
    return completudeDeJoursMesures({
        debut: contexte.debut,
        fin: contexte.fin,
        joursMesures: jours,
        plages: plagesDuSens(plagesDeLaPage.value, direction),
        dernierJourDu: dernierJourDuDeLaBande(direction),
    })
}

/**
 * LE JOUR DE LECTURE, épinglable par les récits ; sinon le jour civil de
 * PARIS, jamais le jour UTC (revue du 21/09, `jourDeLecture`). Aucune page de
 * production ne passe `aujourdHui` : c'est ce repli qui décide tous les jours.
 */
function leJour(): string {
    return props.aujourdHui ?? jourDeLecture()
}

/**
 * LE DERNIER JOUR DÛ DE LA BANDE (lot 6a, volet 2).
 *
 * La bande compte l'ÉNERGIE JOURNALIÈRE quand ce compteur la sert : le délai
 * qui la juge est celui du flux `energie` de CE sens, et d'aucun autre. Quand
 * la bande tombe sur son repli (le premier onglet qui a couvert le contexte),
 * on ne sait pas quel flux la porte : aucun délai n'est alors appliqué, et la
 * bande garde exactement le comportement qu'elle avait.
 */
function dernierJourDuDeLaBande(direction: DirectionEnedis): string | null {
    if (!laBandeEstServieParSonAppel.value) return null
    return dernierJourDuDuFlux(
        fluxDeLaFamille(statut.value, "energie", direction),
        leJour()
    )
}

/**
 * UNE BANDE PAR SENS REGARDÉ (lot 5g), dans l'ordre d'affichage.
 *
 * En « Les deux », la maquette en pose deux, soutirage puis injection, et un
 * compte cumulé au-dessus. En sens unique, il n'y en a qu'une, exactement celle
 * d'avant. Les deux se calculent des mêmes chargements : aucun appel de plus.
 */
const bandesDeCompletude = computed<
    Array<{ direction: DirectionEnedis; completude: Completude; titre: string }>
>(() =>
    directionsRegardees.value.flatMap((direction) => {
        const completude = completudeDuSens(direction)
        if (!completude) return []
        return [
            {
                direction,
                completude,
                titre: lesDeuxRegardes.value
                    ? `${titreDeLaBande.value} · ${LIBELLES_DIRECTION[direction]}`
                    : titreDeLaBande.value,
            },
        ]
    })
)

/**
 * LA BANDE DE RÉFÉRENCE : celle du premier sens regardé. C'est elle qui traduit
 * la fenêtre en index et qui donne aux graphes les jours sans mesure.
 */
const bandeDeCompletude = computed<Completude | null>(
    () => bandesDeCompletude.value[0]?.completude ?? null
)

/**
 * LES JOURS DE LA BANDE D'UN SENS, pour la colonne « Qualité » du tableau
 * (lot 5g). Vide quand ce sens n'est pas regardé : une colonne vide dit « la
 * bande n'a pas parlé », ce qui est exactement le cas.
 */
function joursDeLaBandeDuSens(direction: DirectionEnedis) {
    return (
        bandesDeCompletude.value.find((b) => b.direction === direction)
            ?.completude.jours ?? []
    )
}

/** Vrai quand les deux sens sont regardés ensemble. */
const lesDeuxRegardes = computed(() => directionsRegardees.value.length > 1)

/**
 * LE COMPTE CUMULÉ DES DEUX SENS (lot 5g). Une addition de JOURS MESURÉS, pas
 * d'énergies : les deux sens ne se somment jamais (E5), mais « combien de jours
 * ce compteur a-t-il mesurés, des deux côtés » est une question légitime, et
 * c'est ce que la maquette écrit au-dessus des deux bandes.
 */
const compteCumuleDesDeuxSens = computed<string | null>(() => {
    if (!lesDeuxRegardes.value) return null
    const bandes = bandesDeCompletude.value
    if (bandes.length < 2) return null
    const mesures = bandes.reduce((n, b) => n + b.completude.mesures, 0)
    const attendus = bandes.reduce((n, b) => n + b.completude.attendus, 0)
    return `${mesures} / ${attendus} jours mesurés sur les deux directions`
})

/**
 * LES PLAGES D'ABSENCE, DES DEUX SOURCES DE FAITS (lot 5a, écart D8).
 *
 * Le verdict de workflow nomme les plages d'un compteur en cours de traitement ;
 * le statut agrégé nomme celles de tous les autres — dont les compteurs arrêtés
 * depuis longtemps, sur lesquels le verdict est `null` et tout jour absent
 * tombait en « Manquant » faute d'avoir lu la cause pourtant servie.
 *
 * Les deux listes s'ajoutent sans arbitrage : `etatDuJour` tranche déjà par
 * priorité (`homeys` avant `enedis` avant `manquant`) quand deux plages se
 * recouvrent, et deux faits qui disent la même chose ne se contredisent pas.
 */
const plagesDeLaPage = computed(() => [
    ...plagesDuVerdict(props.verdict),
    ...plagesDuStatut(statut.value),
])

/**
 * LE TITRE DE LA BANDE, QUI DIT CE QU'ELLE COMPTE (lot 5e, écart L1).
 *
 * « Complétude du contexte » tout court laissait trois questions sans réponse :
 * complétude de QUOI, mesurée contre QUOI, et que vient faire ce liseré. Les
 * trois se répondent en une ligne, comme la maquette.
 *
 * La grandeur n'est écrite QUE quand elle est vraie : la bande se calcule sur
 * l'énergie active journalière dès que le compteur la sert, mais un compteur
 * qui ne la sert pas la fait tomber sur le premier onglet qui a couvert le
 * contexte (`retenirLaCompletude`). Dans ce cas, le titre se tait sur la
 * grandeur plutôt que d'en nommer une fausse — « attendu du compteur », lui,
 * reste vrai dans les deux cas : les états viennent du verdict et du statut,
 * jamais d'un seuil du front (E4).
 */
const titreDeLaBande = computed<string>(() =>
    [
        "Complétude du contexte",
        laBandeEstServieParSonAppel.value
            ? "énergie journalière, attendu du compteur"
            : "attendu du compteur",
        "la fenêtre regardée est dessinée dessus",
    ].join(" · ")
)

/** Vrai quand on attend encore le verdict pour pouvoir peindre la bande. */
const bandeEnAttente = computed(
    () => !bandeDeCompletude.value && !!props.contexte
)

/**
 * LES JOURS SANS MESURE DE LA FENÊTRE REGARDÉE (DEV-1508 lot B1).
 *
 * Exactement ceux que la bande hachure, restreints à ce que le graphe montre.
 * La bande couvre le CONTEXTE ; la figure, elle, ne montre que la fenêtre, et
 * peindre un jour hors de ses bornes le collerait contre le bord de l'axe, à une
 * date qui n'est pas la sienne.
 *
 * Une seule source de vérité : ces jours et leurs états SORTENT de la bande. La
 * figure ne recalcule rien et ne peut donc pas la contredire.
 */
const joursAbsentsDeLaFenetre = computed(() => {
    const f = focus.value
    return (bandeDeCompletude.value?.jours ?? [])
        .filter((j) => j.etat !== "mesure")
        .filter((j) => !f || (j.jour >= f.debut && j.jour <= f.fin))
        .map((j) => ({ jour: j.jour, etat: j.etat }))
})

/**
 * LA FENÊTRE REGARDÉE, DITE À LA BANDE (DEV-1508 lot B3).
 *
 * La bande couvre le contexte ; le focus en désigne une tranche. On traduit ce
 * focus en index de jours DE LA BANDE, pour qu'elle marque exactement les jours
 * que les graphes montrent. Une seule fenêtre, deux représentations : après un
 * cliquer-glisser sur un graphe, la bande dit la même chose que l'axe.
 *
 * `null` au contexte : rien à marquer, la bande rend ce qu'elle a toujours rendu.
 */
const indexDeLaFenetre = computed<{ debut: number; fin: number } | null>(() => {
    const f = focus.value
    const jours = bandeDeCompletude.value?.jours
    if (!f || auContexte.value || !jours?.length) return null
    let debut = -1
    let fin = -1
    jours.forEach((j, index) => {
        if (j.jour < f.debut || j.jour > f.fin) return
        if (debut < 0) debut = index
        fin = index
    })
    return debut < 0 ? null : { debut, fin }
})

/**
 * Le contexte dépasse ce que la complétude sait détailler jour par jour. Fait
 * MESURÉ sur les bornes servies, jamais déduit d'une bande absente : une bande
 * absente peut aussi être une bande qui attend son verdict, et les deux ne
 * disent pas la même chose.
 */
const contexteNonDetaillable = computed(
    () => !!props.contexte && joursDeFenetre(props.contexte) > PLAFOND_JOURS
)

/**
 * LE REPLI, pour un compteur qui ne sert PAS l'énergie active journalière.
 *
 * Il ne se déclenche que là, et il n'écrit QU'UNE FOIS par contexte : c'est ce
 * qui répare l'écart D2 dans ce cas aussi. Le compte reste celui du premier
 * onglet qui a couvert le contexte, et ouvrir un autre onglet ne le change plus
 * sous les yeux du client.
 */
function retenirLaCompletude(resultat: ResultatChargement): void {
    const contexte = props.contexte
    if (!contexte) return
    if (laBandeEstServieParSonAppel.value) return
    if (!couvre(resultat.fenetreChargee, contexte)) return
    // UN COMPTE PAR SENS (lot 5g), et un seul écrit par sens et par contexte.
    const retenus = { ...joursMesuresParSens.value }
    let change = false
    for (const { direction, reponses } of resultat.parDirection) {
        if (retenus[direction]) continue
        retenus[direction] = joursMesuresDesReponses(reponses)
        change = true
    }
    if (change) joursMesuresParSens.value = retenus
}

/**
 * L'ONGLET DE RÉFÉRENCE DE LA BANDE : l'énergie, quand ce compteur la sert.
 *
 * On charge la bande SOUS LA CLÉ DE MÉMOIRE DE CET ONGLET, avec ses variables et
 * ses statistiques : au contexte, l'onglet Énergie est servi au grain journalier
 * lui aussi, et il lit alors le bloc déjà en mémoire. Un seul appel part pour
 * les deux (E6, E7). Une demande plus étroite retenue sous cette clé aurait
 * privé l'onglet de ses autres séries.
 */
const ongletDeLaBande = computed<OngletResolu | null>(
    () => onglets.value.find((o) => o.cle === "energie") ?? null
)

const laBandeEstServieParSonAppel = computed(
    () => ongletDeLaBande.value !== null
)

/**
 * LA BANDE SE CALCULE UNE FOIS, POUR ELLE-MÊME (lot 5a, écart D2).
 *
 * Elle ne dépend pas de l'onglet ouvert : elle porte la complétude du CONTEXTE
 * sur l'énergie active journalière, et elle ne bouge plus tant que le contexte
 * ne bouge pas. Elle est lue POUR CHAQUE SENS porté par le compteur (lot 5g),
 * qu'il soit regardé ou non : changer de position du sélecteur ne coûte alors
 * aucun appel de plus, et les deux bandes de « Les deux » sont déjà là.
 *
 * Le grain est FORCÉ au jour (`grains: ["daily"]`) : la bande est une bande de
 * JOURS. Le niveau que la largeur imposerait ne la concerne pas.
 */
async function chargerLaBande(): Promise<void> {
    const contexte = props.contexte
    const onglet = ongletDeLaBande.value
    if (!contexte || !onglet || !props.idSource) return
    const directions = props.directions
    if (directions.length === 0) return

    // L'onglet Énergie SERA-T-IL servi au jour sur ce contexte ? Si oui, la
    // bande demande exactement ce que l'onglet demandera, sous SA clé : un seul
    // appel pour les deux. Sinon (contexte court, servi au pas natif ou à
    // l'heure), la bande a sa propre clé et ne demande QUE sa référence — une
    // demande étroite retenue sous la clé de l'onglet priverait celui-ci de ses
    // autres séries.
    const partagee =
        grainRetenu(
            niveauPourFenetre(joursDeFenetre(contexte)),
            grainsPourLOnglet(onglet, contexte)
        ) === "daily"

    let resultat: ResultatChargement
    try {
        resultat = await charger({
            idSource: props.idSource,
            onglet: partagee ? onglet.cle : "bande",
            directions,
            // CHAQUE SENS DEMANDE LES SIENNES (lot 5g) : l'union des deux
            // partait pour chacun, le lecteur refusait, et la bande restait en
            // squelette sans fin sur tout compteur à deux sens.
            variables: partagee
                ? parSens(directions, (d) => variablesDeLOnglet(onglet, d))
                : parSens(directions, (d) => [variableDeLaBande(d)]),
            stats: partagee
                ? parSens(directions, (d) => statsDeLOnglet(onglet, d))
                : ["sum"],
            contexte,
            fenetre: contexte,
            grains: ["daily"],
        })
    } catch {
        // La bande reste muette : une bande vide dirait « zéro jour mesuré », ce
        // qui n'est pas ce qu'on a lu.
        return
    }
    // LA BANDE DE CHAQUE SENS SE LIT DE SES PROPRES RÉPONSES (lot 5g) : fondre
    // les deux ferait compter un jour mesuré en injection comme un jour mesuré
    // en soutirage, et les quatre jours manquants d'une injection en retard
    // disparaîtraient de la bande.
    const retenus = { ...joursMesuresParSens.value }
    let change = false
    for (const { direction, reponses } of resultat.parDirection) {
        const jours = joursMesuresDeLaBande(reponses, [
            variableDeLaBande(direction),
        ])
        if (jours === null) continue
        retenus[direction] = jours
        change = true
    }
    if (change) joursMesuresParSens.value = retenus
}

/**
 * LE CONTEXTE A CHANGÉ : la bande se relit.
 *
 * Il change réellement en cours de route — les informations du compteur arrivent
 * après le premier rendu, et la première donnée détenue recule alors le début.
 * Sans cette relecture, la bande garderait les jours lus sur l'ancien contexte
 * et peindrait « Manquant » sur des jours qu'elle n'a jamais regardés. La
 * mémoire du composable rend zéro appel quand la nouvelle fenêtre est déjà
 * couverte.
 */
/**
 * UN CHANGEMENT DE CONTEXTE REFAIT TOUT L'ÉCRAN, EN UN SEUL CHARGEMENT.
 *
 * Le contexte change pour deux raisons, et la réponse est la même : les
 * informations du compteur arrivent après le premier rendu et reculent le début
 * (première donnée détenue), ou un RACCOURCI a été pressé (lot 5c).
 *
 * Ce qui repart : la bande (ses jours ne sont plus les mêmes), puis l'onglet
 * regardé — dans CET ordre, et jamais les deux en parallèle, parce qu'au
 * journalier l'onglet Énergie lit le bloc que la bande vient de retenir sous la
 * même clé. La mémoire du composable rend zéro appel quand la nouvelle fenêtre
 * est déjà couverte : revenir à « 3 mois » après « 1 an » ne coûte rien.
 *
 * LE FOCUS REVIENT AU CONTEXTE. Une fenêtre regardée héritée du contexte
 * d'avant peut n'avoir plus aucun jour dans le nouveau (passer de « 1 an » à
 * « 7 jours » en regardant janvier) : l'écran resterait vide en annonçant une
 * fenêtre que le contexte ne contient plus.
 *
 * L'ANTI-REBOND vaut pour la rafale de clics, pas pour le premier : trois
 * raccourcis pressés en une seconde ne partent qu'une fois, sur le dernier.
 */
const ANTI_REBOND_CONTEXTE_MS = 150
let minuterieDuContexte: ReturnType<typeof setTimeout> | null = null

watch(
    () => props.contexte,
    (apres, avant) => {
        if (!apres || memeFenetre(apres, avant ?? null)) return
        joursMesuresParSens.value = {}
        // Le focus suit le contexte, sans passer par `recadrer` : il n'y a pas
        // de geste de recadrage à tracer ici, et le chargement qui suit est
        // celui du contexte entier, une seule fois.
        focus.value = apres
        if (minuterieDuContexte) clearTimeout(minuterieDuContexte)
        minuterieDuContexte = setTimeout(() => {
            minuterieDuContexte = null
            void chargerLaBande().then(() => chargerLOnglet())
        }, ANTI_REBOND_CONTEXTE_MS)
    }
)

/**
 * UN RACCOURCI DE CONTEXTE (écart B3, décision Q2 du 15/09).
 *
 * Il ne calcule aucune date : il demande une LARGEUR à l'écran appelant, qui la
 * repasse par `contexteDeLaPageCompteur` — la seule fonction qui sait où le dû
 * s'arrête. La fin reste le dernier jour dû, le début recule.
 */
function choisirLeContexte(jours: number): void {
    const avant = props.joursContexte
    detecteur.clic(`contexte:${jours}`, Date.now(), avant !== jours)
    if (avant === jours) return
    emit("update:joursContexte", jours)
    if (props.idSource) {
        track({
            event: "Compteur Contexte Changé",
            payload: {
                source_id: props.idSource,
                contexte_jours_avant: avant,
                contexte_jours_apres: jours,
            },
        })
    }
}

/** Ce que chaque onglet a chargé, par clé d'onglet. */
const charges = ref<Record<string, ResultatChargement>>({})

/**
 * LES ONGLETS DONT LA LECTURE A ÉCHOUÉ.
 *
 * Une lecture qui échoue et une grandeur que le compteur ne publie pas donnent
 * le même écran : une carte sans courbe. Ce ne sont pas les mêmes faits, et
 * les confondre serait la 18ᵉ forme de garde qui ment
 * (`top/docs/methode-verification-donnees.md` §12). Le graphe reçoit donc de
 * quoi choisir sa phrase.
 */
const echecs = ref<Record<string, boolean>>({})

const echecDeLecture = computed(() =>
    ongletActif.value ? (echecs.value[ongletActif.value] ?? false) : false
)

const resultatCourant = computed<ResultatChargement | null>(() =>
    ongletActif.value ? (charges.value[ongletActif.value] ?? null) : null
)

const niveau = computed<NiveauSeries | null>(
    () => resultatCourant.value?.niveau ?? null
)

/** Le pas de la courbe du compteur, en minutes. Lu du référentiel, jamais deviné. */
const minutesPasCompteur = computed(() =>
    minutesDuPas(
        props.informations?.data?.soutirage?.cdc?.pas ??
            props.informations?.data?.injection?.cdc?.pas
    )
)

// ============================================================================
// LE NIVEAU, DIT À L'ÉCRAN (lot 5c, écart T4, décision Q8 du 15/09)
// ============================================================================
//
// Le niveau se CALCULAIT depuis le lot 2 et ne s'écrivait nulle part : celui qui
// regarde une courbe d'un an ne pouvait pas savoir qu'il lisait un agrégat
// journalier, ni qu'un cliquer-glisser sur deux semaines lui rendrait le pas du
// compteur. La règle est écrite une fois, là où elle s'applique.
//
// ⚠️ Le libellé se déduit de la LARGEUR REGARDÉE, pas du niveau servi : quand un
// compteur ne détient pas le grain que la largeur appelle (`grainRetenu`), c'est
// le grain SERVI qui s'écrit, et il ne se déguise pas en celui de la règle.

/** Le libellé du niveau, tel que la barre l'écrit. `null` sans fenêtre. */
const libelleNiveau = computed<string | null>(() => {
    const f = focus.value
    const servi = niveau.value
    if (!f || !servi) return null
    const jours = joursDeFenetre(f)

    // LA BANDE DE LA RÈGLE NE S'ÉCRIT QUE SI LA RÈGLE A ÉTÉ SUIVIE.
    //
    // Le grain SERVI n'est pas toujours celui que la largeur appelle : un
    // compteur qui ne détient pas l'énergie au pas de la courbe est servi à
    // l'heure même sur sept jours (`grainRetenu`). Écrire alors « (15 à 92 j) »
    // sur une fenêtre de sept jours serait un fait faux, dit avec aplomb.
    const suitLaRegle = niveauPourFenetre(jours) === servi

    if (servi === "natif") {
        const pas = minutesPasCompteur.value
        const nom = pas ? `courbe native ${pas} min` : "courbe native"
        return suitLaRegle ? `${nom} (≤ ${SEUIL_NATIF_JOURS} j)` : nom
    }
    if (servi === "hourly") {
        const nom = "enveloppe horaire, moyenne, min, max"
        return suitLaRegle ? `${nom} (15 à ${SEUIL_HORAIRE_JOURS} j)` : nom
    }
    if (servi === "daily") {
        const nom = suitLaRegle
            ? `journalier (> ${SEUIL_HORAIRE_JOURS} j)`
            : "journalier"
        return jours > SEUIL_SEMAINE_JOURS ? `${nom}, énergie en semaines` : nom
    }
    // Un grain que la règle n'appelle jamais (périodes de facturation, mois) :
    // on écrit ce qui est SERVI, jamais le niveau que la largeur aurait voulu.
    return LIBELLES_NIVEAU_SERVI[servi]
})

/**
 * LE NIVEAU TEL QUE LA CARTE L'ÉCRIT, sans la fenêtre qui le déclenche.
 *
 * LE FAIT, relevé sur la capture du lot 5d1 à 1 366 px : « Niveau : enveloppe
 * horaire, moyenne, min, max (15 à 92 j) » dans une puce, à côté du titre,
 * poussait « Enveloppe horaire (kW) » sur deux lignes.
 *
 * La RÈGLE (« de 15 à 92 jours, c'est ce niveau-là ») se lit dans la ligne
 * Fenêtre, juste au-dessus, à côté de la largeur qui la décide — c'est là
 * qu'elle a un sens. La puce de la carte n'a qu'à dire CE QU'ON REGARDE.
 */
const libelleNiveauCourt = computed<string | null>(() =>
    libelleNiveau.value
        ? libelleNiveau.value.replace(/\s*\([^)]*\)\s*$/, "")
        : null
)

/** Les grains hors règle, écrits avec les mots du client. */
const LIBELLES_NIVEAU_SERVI: Record<NiveauSeries, string> = {
    natif: "courbe native",
    hourly: "horaire",
    daily: "journalier",
    weekly: "hebdomadaire",
    monthly: "mensuel",
    periode: "par période de facturation",
}

const graphes = computed(() => {
    const onglet = ongletCourant.value
    const resultat = resultatCourant.value
    if (!onglet || !resultat) return []
    return graphesDeLOnglet({
        onglet,
        parDirection: resultat.parDirection,
        // Le NIVEAU décide de ce que « maximum » veut dire : à l'heure, c'est
        // le maximum des puissances moyennes sur le pas du compteur, pas la
        // puissance maximale instantanée (décision du 11/09).
        grain: resultat.niveau,
        minutesPasCompteur: minutesPasCompteur.value,
        // Au-delà d'un an, l'énergie journalière se peint en semaines (7.5) :
        // c'est la largeur REGARDÉE qui le décide, pas le contexte chargé.
        joursRegardes: focus.value ? joursDeFenetre(focus.value) : null,
    })
})

// ============================================================================
// LA CARTE DE DÉTAIL HORAIRE DE L'ÉNERGIE (lot 5d, écart G4, décision Q5)
// ============================================================================
//
// Ce que le client y gagne : la carte journalière répond à « quels jours », la
// carte horaire à « à quelle heure ». Sur trois mois, la première est lisible et
// la seconde ne l'est pas ; sur sept jours, les deux le sont. La maquette les
// pose CÔTE À CÔTE, et c'est ce qui permet de lire un profil sans rien recadrer.
//
// TROIS PRÉCAUTIONS, chacune contre un mot faux :
//
//  • SEPT DERNIERS JOURS **MESURÉS**, pas sept derniers jours. Sur un compteur
//    arrêté, les sept derniers jours du calendrier sont vides : la carte
//    montrerait une figure blanche et le titre annoncerait une donnée. Les jours
//    sont LUS dans ce que l'onglet vient de charger.
//  • UN SEUL APPEL, 168 points par sens, retenu sous sa propre clé de mémoire :
//    revenir sur l'onglet ne le repaie pas (E6, E7).
//  • « · Calculé » N'EST PAS DÉCORATIF. Il ne s'écrit que si l'origine LUE des
//    points le dit. La maquette l'écrit toujours ; nous, seulement quand c'est
//    vrai (charte, art. 3).

/** La clé d'onglet du détail horaire : sa propre mémoire, jamais celle d'Énergie. */
const ONGLET_DETAIL_HORAIRE = "energie-detail-horaire"

/** Combien de jours mesurés la carte de détail montre. */
const JOURS_DU_DETAIL_HORAIRE = 7

/** Ce que le détail horaire a chargé, et sur quelle fenêtre. */
const detailHoraire = ref<{
    fenetre: Fenetre
    resultat: ResultatChargement
} | null>(null)

/** Vrai quand la carte de détail a lieu d'être : l'énergie, au-delà de 14 jours. */
const detailHoraireAttendu = computed(
    () =>
        ongletCourant.value?.cle === "energie" &&
        !!focus.value &&
        joursDeFenetre(focus.value) > SEUIL_NATIF_JOURS
)

/**
 * Les SEPT derniers jours mesurés de la fenêtre regardée, lus dans ce que
 * l'onglet vient de charger. `null` quand aucun jour n'est mesuré : il n'y a
 * alors rien à détailler, et on ne demande rien.
 */
function fenetreDuDetailHoraire(
    resultat: ResultatChargement,
    fenetre: Fenetre
): Fenetre | null {
    const jours = joursMesuresDesReponses(
        resultat.parDirection.flatMap((p) => p.reponses)
    ).filter((j) => j >= fenetre.debut && j <= fenetre.fin)
    if (jours.length === 0) return null
    const retenus = jours.slice(-JOURS_DU_DETAIL_HORAIRE)
    return { debut: retenus[0], fin: retenus[retenus.length - 1] }
}

async function chargerLeDetailHoraire(
    resultat: ResultatChargement
): Promise<void> {
    const contexte = props.contexte
    const fenetre = focus.value
    if (!detailHoraireAttendu.value || !contexte || !fenetre || !props.idSource)
        return
    const directions = directionsRegardees.value
    if (directions.length === 0) return
    const cible = fenetreDuDetailHoraire(resultat, fenetre)
    if (!cible) {
        detailHoraire.value = null
        return
    }
    if (
        detailHoraire.value &&
        memeFenetre(detailHoraire.value.fenetre, cible)
    ) {
        return
    }
    try {
        const detail = await charger({
            idSource: props.idSource,
            onglet: ONGLET_DETAIL_HORAIRE,
            directions,
            variables: parSens(directions, (d) => [variableDeLaBande(d)]),
            stats: ["sum"],
            // Le contexte du détail EST sa fenêtre : sans cela, l'élargissement
            // d'un quart déborderait sur des jours que la carte n'annonce pas.
            contexte: cible,
            fenetre: cible,
            grains: ["hourly"],
        })
        detailHoraire.value = { fenetre: cible, resultat: detail }
    } catch {
        // Le détail est un COMPLÉMENT : son échec ne doit pas effacer la carte
        // journalière, qui, elle, est chargée.
        detailHoraire.value = null
    }
}

/**
 * La carte de détail horaire, quand elle a de quoi se peindre. Son titre est
 * écrit ICI parce qu'il porte deux faits que le rangeur ne connaît pas : le
 * nombre de jours réellement montrés, et l'origine lue des points.
 */
const grapheDuDetailHoraire = computed<GrapheEnedis | null>(() => {
    const onglet = ongletCourant.value
    const detail = detailHoraire.value
    if (!onglet || !detail || !detailHoraireAttendu.value) return null
    const graphes = graphesDeLOnglet({
        onglet,
        parDirection: detail.resultat.parDirection,
        grain: detail.resultat.niveau,
        minutesPasCompteur: minutesPasCompteur.value,
        joursRegardes: joursDeFenetre(detail.fenetre),
    })
    const graphe = graphes.find((g) => g.series.length > 0)
    if (!graphe) return null
    const jours = joursDeFenetre(detail.fenetre)
    const calcule = graphe.series.every((s) => s.origine === "calcul")
    return {
        ...graphe,
        cle: `${graphe.cle}-detail-horaire`,
        titre: `Énergie horaire (${graphe.unite ?? "kWh"}), ${jours} derniers jours mesurés${calcule ? " · Calculé" : ""}`,
    }
})

// ============================================================================
// LE PAS DE TEMPS, CARTE PAR CARTE (R1 et R7, revue du 17/09)
// ============================================================================
//
// INTENTION, mot pour mot : « On voit rien il faut mieux pouvoir switcher entre
// données horaires et journalières quand la période le permet », et « si la
// période le permet il faut pouvoir switcher sur l'affichage de la CDC ». Sur
// trois mois, la seule façon de voir une courbe de charge était de resserrer la
// fenêtre à deux semaines, donc de perdre de vue les trois mois. Le pas devient
// un choix de LECTURE, carte par carte, qui ne touche pas la fenêtre regardée.
//
// CE QUI NE CHANGE PAS TANT QUE PERSONNE NE CHOISIT : rien. Sans choix, la
// carte est servie par le chargement de l'onglet, au niveau que la largeur
// impose, exactement comme avant ce lot.
//
// CE QUE COÛTE UN CHOIX : un appel par sens, et rien d'autre. La carte charge
// SES variables, à SON grain, sous SA clé de mémoire, et sa fenêtre est son
// propre contexte — sans quoi l'élargissement d'un quart la ferait déborder du
// plafond dapi et découper en deux appels (règle `pasPermis`, qui offre
// précisément les grains qui tiennent d'un seul appel).

/** Le pas VOULU par le lecteur, par identité de carte. Vide = pas de l'onglet. */
const pasVoulu = ref<Record<string, GrainSeries>>({})

/** Ce qu'un pas choisi a chargé. Rangé par identité de carte, jamais par clé. */
const chargeParCarte = ref<
    Record<
        string,
        { grain: GrainSeries; fenetre: Fenetre; resultat: ResultatChargement }
    >
>({})

/** La largeur regardée, en jours, ou `null` : la règle du pas en a besoin. */
const joursRegardes = computed<number | null>(() =>
    focus.value ? joursDeFenetre(focus.value) : null
)

/** Les séries d'onglet que porte cette carte, quel que soit son grain. */
function seriesDeCetteCarte(graphe: GrapheEnedis) {
    const onglet = ongletCourant.value
    return onglet ? seriesDeLaCarte(onglet, graphe) : []
}

/** Les pas offerts sur cette carte, tels que la règle les calcule. */
function pasPermisDeLaCarte(graphe: GrapheEnedis): GrainSeries[] {
    // La carte de détail horaire est une carte À PAS FIXE : son titre annonce
    // sept jours à l'heure, et lui donner un sélecteur ferait mentir ce titre.
    if (estLaCarteDeDetail(graphe)) return []
    return pasPermis({
        series: seriesDeCetteCarte(graphe),
        joursRegardes: joursRegardes.value,
    })
}

/**
 * LE PAS RÉELLEMENT SERVI SUR CETTE CARTE.
 *
 * Sans choix, c'est celui de l'onglet. Avec un choix devenu impossible (on a
 * élargi au-delà du plafond de son grain), c'est le pas permis LE PLUS PROCHE :
 * le choix n'est pas oublié pour autant, il reprend dès qu'il redevient permis.
 */
function pasServiDeLaCarte(graphe: GrapheEnedis): GrainSeries | null {
    const voulu = pasVoulu.value[identiteDeLaCarte(graphe)]
    if (!voulu) return grainDeLaCarte(graphe)
    return (
        pasLePlusProche(voulu, pasPermisDeLaCarte(graphe)) ??
        grainDeLaCarte(graphe)
    )
}

/** Pourquoi le pas voulu n'est pas celui qui est servi. `null` = rien à dire. */
function raisonDuPasDeLaCarte(graphe: GrapheEnedis): string | null {
    const voulu = pasVoulu.value[identiteDeLaCarte(graphe)]
    if (!voulu) return null
    return raisonDuRepli({
        voulu,
        servi: pasServiDeLaCarte(graphe),
        series: seriesDeCetteCarte(graphe),
        joursRegardes: joursRegardes.value,
    })
}

/** La clé de mémoire d'une carte à pas choisi : la sienne, jamais celle de l'onglet. */
function ongletDeLaCarte(graphe: GrapheEnedis): string {
    return `${ongletCourant.value?.cle ?? ""}#${identiteDeLaCarte(graphe)}`
}

/**
 * LE NUMÉRO DE LA DERNIÈRE DEMANDE, PAR CARTE.
 *
 * Deux clics rapides sur deux pas partent tous les deux ; rien n'oblige le
 * lecteur à répondre dans l'ordre. Sans ce jeton, la réponse du PREMIER pas
 * pouvait arriver en dernier et se peindre sous un sélecteur qui en annonce un
 * autre. Même règle que `derniereDemande` pour l'onglet, tenue par carte.
 */
const derniereDemandeParCarte = new Map<string, number>()

/**
 * Charge CETTE carte à CE pas. Un appel par sens, sous sa propre clé : la
 * mémoire du composable rend zéro appel si on revient sur un pas déjà vu.
 */
async function chargerLaCarte(
    graphe: GrapheEnedis,
    pas: GrainSeries
): Promise<void> {
    const onglet = ongletCourant.value
    const fenetre = focus.value
    if (!onglet || !fenetre || !props.idSource) return
    const series = seriesDeCetteCarte(graphe)
    const directions = DIRECTIONS.filter((d) =>
        series.some((s) => s.direction === d)
    )
    if (directions.length === 0) return
    const identite = identiteDeLaCarte(graphe)
    const jeton = (derniereDemandeParCarte.get(identite) ?? 0) + 1
    derniereDemandeParCarte.set(identite, jeton)
    const debut = Date.now()
    let resultat: ResultatChargement
    try {
        resultat = await charger({
            idSource: props.idSource,
            onglet: ongletDeLaCarte(graphe),
            directions,
            variables: parSens(directions, (d) => [
                ...new Set(
                    series
                        .filter((s) => s.direction === d)
                        .map((s) => s.variable)
                ),
            ]),
            stats: parSens(directions, (d) => [
                ...new Set(
                    series
                        .filter((s) => s.direction === d)
                        .flatMap((s) => s.stats)
                ),
            ]),
            // LA FENÊTRE EST SON PROPRE CONTEXTE : pas d'élargissement d'un
            // quart, donc pas de débordement de plafond, donc UN appel.
            contexte: fenetre,
            fenetre,
            grains: [pas],
        })
    } catch {
        // L'échec d'une carte ne défait pas l'onglet : elle garde le pas de
        // l'onglet, qui, lui, est chargé.
        if (derniereDemandeParCarte.get(identite) !== jeton) return
        const sansElle = { ...chargeParCarte.value }
        delete sansElle[identite]
        chargeParCarte.value = sansElle
        return
    }
    // Doublé par un clic plus récent sur cette carte : ce résultat est périmé,
    // il ne se peint pas et ne se compte pas.
    if (derniereDemandeParCarte.get(identite) !== jeton) return
    chargeParCarte.value = {
        ...chargeParCarte.value,
        [identite]: { grain: resultat.niveau, fenetre, resultat },
    }
    for (const direction of directions) {
        track({
            event: "Compteur Onglet Chargé",
            payload: {
                source_id: props.idSource,
                onglet: onglet.cle,
                direction,
                niveau: resultat.niveau,
                // LE PAS CHOISI, à côté du niveau : le niveau dit ce qui est
                // servi, `pas` dit que c'est un CHOIX et lequel (R1).
                pas,
                fenetre_jours: joursDeFenetre(fenetre),
                duree_ms: Date.now() - debut,
                nb_appels: resultat.appels,
                points_servis: compterLesPoints(resultat, direction),
                depuis_memoire: resultat.depuisMemoire,
            },
        })
    }
}

/**
 * Le lecteur a choisi un pas sur cette carte.
 *
 * REVENIR AU PAS DE L'ONGLET NE COÛTE RIEN : ces points-là sont déjà chargés,
 * et repartir les chercher sous la clé de la carte paierait deux fois la même
 * réponse. Le choix reste noté — il redeviendra un chargement dès que la
 * fenêtre l'éloignera du pas de l'onglet.
 */
async function choisirLePas(
    graphe: GrapheEnedis,
    pas: GrainSeries
): Promise<void> {
    const identite = identiteDeLaCarte(graphe)
    pasVoulu.value = { ...pasVoulu.value, [identite]: pas }
    if (pas === niveau.value) {
        const sansElle = { ...chargeParCarte.value }
        delete sansElle[identite]
        chargeParCarte.value = sansElle
        return
    }
    await chargerLaCarte(graphe, pas)
}

/**
 * APRÈS UN RECADRAGE, CHAQUE CHOIX EST REJOUÉ (R1 : « le choix survit au brush
 * tant qu'il reste permis »).
 *
 * Trois issues par carte : le pas voulu est toujours permis et se recharge sur
 * la nouvelle fenêtre ; il ne l'est plus et la carte retombe sur celui de
 * l'onglet, en disant pourquoi ; le pas servi EST celui de l'onglet, et il n'y
 * a alors rien à charger du tout.
 */
async function rejouerLesPasChoisis(): Promise<void> {
    if (Object.keys(pasVoulu.value).length === 0) return
    for (const graphe of graphes.value) {
        const identite = identiteDeLaCarte(graphe)
        if (!pasVoulu.value[identite]) continue
        const servi = pasServiDeLaCarte(graphe)
        const dejaLa = chargeParCarte.value[identite]
        if (!servi || servi === niveau.value) {
            // Le pas servi est celui de l'onglet : la carte revient au
            // chargement commun plutôt que d'en payer un second, identique.
            if (dejaLa) {
                const sansElle = { ...chargeParCarte.value }
                delete sansElle[identite]
                chargeParCarte.value = sansElle
            }
            continue
        }
        if (
            dejaLa &&
            dejaLa.grain === servi &&
            focus.value &&
            memeFenetre(dejaLa.fenetre, focus.value)
        ) {
            continue
        }
        await chargerLaCarte(graphe, servi)
    }
}

/**
 * LA CARTE, PEINTE AU PAS QU'ELLE SERT.
 *
 * Les points viennent du chargement de la carte quand elle en a un, et le
 * rangement en graphes est REFAIT sur ces points-là : l'unité, le titre et la
 * nature d'un maximum dépendent du grain (des kVA relevés au jour, des kW
 * dérivés à l'heure), et les recopier du graphe de l'onglet écrirait le titre
 * d'un grain sur les points d'un autre.
 */
function carteAuPasServi(graphe: GrapheEnedis): GrapheEnedis {
    const onglet = ongletCourant.value
    const charge = chargeParCarte.value[identiteDeLaCarte(graphe)]
    if (!onglet || !charge) return graphe
    // UNE CHARGE D'UNE AUTRE FENÊTRE NE SE PEINT PAS. Entre un recadrage et le
    // rejeu du pas choisi, la carte tiendrait sinon les points de la fenêtre
    // d'avant sur l'axe de celle d'après : la carte de l'onglet, elle, est
    // déjà à jour, et vaut mieux qu'un instantané faux.
    if (!focus.value || !memeFenetre(charge.fenetre, focus.value)) return graphe
    const refaits = graphesDeLOnglet({
        onglet: { ...onglet, series: seriesDeCetteCarte(graphe) },
        parDirection: charge.resultat.parDirection,
        grain: charge.resultat.niveau,
        minutesPasCompteur: minutesPasCompteur.value,
        joursRegardes: joursRegardes.value,
    })
    return (
        refaits.find(
            (g) => identiteDeLaCarte(g) === identiteDeLaCarte(graphe)
        ) ?? graphe
    )
}

/** Les cartes de graphe du panneau : celles de l'onglet, plus le détail horaire. */
const cartesDuPanneau = computed<GrapheEnedis[]>(() => {
    const cartes = graphes.value.map(carteAuPasServi)
    return grapheDuDetailHoraire.value
        ? [...cartes, grapheDuDetailHoraire.value]
        : cartes
})

/**
 * LA CARTE DE DÉTAIL A SA PROPRE FENÊTRE, SON PROPRE GRAIN, SES PROPRES TROUS.
 *
 * Sans cela, elle héritait de la fenêtre de l'onglet : un axe de trois mois pour
 * sept jours de points, le format de date du journalier sur des pas horaires, et
 * les quatre-vingt-cinq jours absents du contexte peints sur une figure qui n'en
 * montre que sept. Une carte qui annonce sept jours doit être bornée à sept
 * jours — sinon son titre ment sur ce que sa figure dessine.
 */
function estLaCarteDeDetail(graphe: GrapheEnedis): boolean {
    return graphe.cle === grapheDuDetailHoraire.value?.cle
}

function fenetreDeLaCarte(graphe: GrapheEnedis): Fenetre | null {
    return estLaCarteDeDetail(graphe)
        ? (detailHoraire.value?.fenetre ?? null)
        : focus.value
}

function grainDeLaCarte(graphe: GrapheEnedis) {
    if (estLaCarteDeDetail(graphe)) {
        return detailHoraire.value?.resultat.niveau ?? null
    }
    // Le pas CHOISI fait foi sur cette carte : c'est lui qui a servi ses
    // points, et le format des dates de son axe s'en déduit.
    return (
        chargeParCarte.value[identiteDeLaCarte(graphe)]?.grain ?? niveau.value
    )
}

/**
 * LA PUCE DE NIVEAU N'APPARAÎT QU'UNE FOIS (Q8).
 *
 * L'onglet Puissance d'un C4 porte trois cartes (active, réactive, apparente) :
 * répéter la puce sur chacune ferait trois fois la même phrase. Elle vit sur la
 * PREMIÈRE carte, comme la barre de niveau de la maquette, qui en a une seule
 * pour tout l'onglet.
 */
function porteLeNiveau(graphe: GrapheEnedis): boolean {
    return (
        ongletCourant.value?.cle === "puissance" &&
        graphe.cle === cartesDuPanneau.value[0]?.cle
    )
}

function joursAbsentsDeLaCarte(graphe: GrapheEnedis) {
    const fenetre = fenetreDeLaCarte(graphe)
    if (!fenetre) return joursAbsentsDeLaFenetre.value
    return joursAbsentsDeLaFenetre.value.filter(
        (j) => j.jour >= fenetre.debut && j.jour <= fenetre.fin
    )
}

/**
 * Vrai quand l'onglet regardé peint son énergie en semaines. Le tableau suit :
 * ses lignes sont celles du graphe, il ne redécoupe pas au jour des points
 * hebdomadaires (ce qui écrirait une ligne par lundi sous le titre « au jour »).
 */
const energieHebdomadaire = computed(() => {
    const onglet = ongletCourant.value
    if (!onglet) return false
    return energieEnSemaines({
        onglet,
        grain: niveau.value,
        joursRegardes: focus.value ? joursDeFenetre(focus.value) : null,
    })
})

/**
 * Le grain du dépliant : le plus fin qui tient dans 750 lignes (E2).
 *
 * Un onglet servi au grain `periode` ou `monthly` n'a pas de grain de tableau à
 * lui : chacun de ses points EST une ligne, datée de son début. On les lit donc
 * au jour, ce qui rend exactement une ligne par période ou par mois.
 */
const grainServi = computed<GrainTableau>(() => {
    if (energieHebdomadaire.value) return "weekly"
    const servi = niveau.value
    if (servi === "periode" || servi === "monthly") return "daily"
    return (servi ?? "daily") as GrainTableau
})

// ============================================================================
// LE RAPPEL DE COMPLÉTUDE ET LA FRAÎCHEUR, SOUS L'ONGLET (lot 5d, G1 et G2)
// ============================================================================
//
// La bande est en haut de l'écran ; la carte de graphe est en bas. Entre les
// deux, à 1 366 × 768, il y a un écran de défilement. Celui qui ouvre
// « Puissance » ne sait plus ce que ce compteur a reçu sur ce qu'il regarde, et
// il ne le saura pas en regardant une courbe — une courbe s'arrête, elle ne dit
// pas pourquoi.
//
// ⚠️ CE QUI EST COMPTÉ EST CE QUI EST ÉCRIT : les jours de la FENÊTRE REGARDÉE,
// pour les séries de CET onglet. Pas le contexte (on n'a pas chargé au-delà de
// la fenêtre élargie), pas les autres onglets (ils n'ont pas les mêmes flux).
// C'est exactement le compte que la bande donne quand on regarde tout le
// contexte, et c'est le compte de ce qu'on regarde quand on a resserré.

/** La complétude des séries de l'onglet regardé, sur la fenêtre regardée. */
const completudeDeLOnglet = computed<Completude | null>(() => {
    const resultat = resultatCourant.value
    const fenetre = focus.value
    // MÊME GARDE QUE LA BANDE (décision du 11/09) : sans le verdict, tout jour
    // sans point tombe en « Manquant ». Le rappel annoncerait « 7 Manquant »
    // puis se renierait en « 7 Collecte » une seconde plus tard, à l'arrivée du
    // verdict. Un mot d'état affiché puis démenti apprend quelque chose de faux.
    if (!props.verdictRecu || !resultat || !fenetre) return null
    return completudeDeJoursMesures({
        debut: fenetre.debut,
        fin: fenetre.fin,
        joursMesures: joursMesuresDesReponses(
            resultat.parDirection.flatMap((p) => p.reponses)
        ).filter((j) => j >= fenetre.debut && j <= fenetre.fin),
        plages: plagesDeLaPage.value,
        dernierJourDu: dernierJourDuDeLOnglet.value,
    })
})

/**
 * LE DERNIER JOUR DÛ DE L'ONGLET REGARDÉ (lot 6a, volet 2), lu du flux de SA
 * famille, dans le sens regardé.
 *
 * DEUX SENS REGARDÉS ENSEMBLE : le rappel n'a qu'une phrase pour les deux, et
 * ils n'ont pas forcément la même promesse. On retient alors le PLUS STRICT
 * des deux jours dus — le plus récent — et il faut que les DEUX soient promis :
 * un côté muet ne se laisse pas juger par la promesse de l'autre.
 *
 * Les familles sans flux dû (dépassements, facturation, index) n'ont pas de
 * promesse : `null`, et le rappel garde le comportement d'avant.
 */
const dernierJourDuDeLOnglet = computed<string | null>(() => {
    const cle = ongletCourant.value?.cle
    if (!cle) return null
    const jour = leJour()
    const jours = directionsRegardees.value.map((direction) =>
        dernierJourDuDuFlux(fluxDeLaFamille(statut.value, cle, direction), jour)
    )
    if (jours.length === 0 || jours.some((j) => j === null)) return null
    return (jours as string[]).reduce((a, b) => (a > b ? a : b))
})

/**
 * La phrase du rappel. `null` quand l'onglet n'en a pas (Facturation : une
 * période close n'est pas un jour) ou quand rien n'a encore été chargé.
 */
const rappelDeLOnglet = computed<string | null>(() => {
    const onglet = ongletCourant.value
    const completude = completudeDeLOnglet.value
    if (!onglet || !completude) return null
    // Le libellé vient de l'ONGLET RÉSOLU, pas de la table : sur un onglet qui
    // n'a que de l'injection à montrer, il le DIT (« Énergie injectée »,
    // « Courbe d'injection »), lot 5g.
    const libelle = onglet.libelleRappel
    if (!libelle) return null
    // Les dépassements ont leur propre phrase : une absence y est ambiguë, et
    // « n Manquant » la tranche dans le sens de la panne (lot 5d4).
    return onglet.cle === "depassements"
        ? texteDuRappelDesDepassements(
              libelle,
              completude,
              dateFr(derniereValeurDeLaFamille.value)
          )
        : texteDuRappel(libelle, completude)
})

/**
 * LA LIGNE COURTE DE FRAÎCHEUR, à côté du rappel (lot 5d4).
 *
 * ⚠️ ELLE NE LIT PLUS L'AGRÉGAT DU COMPTEUR. Ce qu'elle porte vient des BORNES
 * SERVIES POUR CETTE FAMILLE : `data_retrieval.status` date le flux principal,
 * et le recopier écrivait « 15/09/2026 · attendu J+3 · à jour » sous une carte
 * de dépassements figée depuis le 16/01/2026 (mesuré en prod le 16/09 sur le C2
 * `6a8eebfa`). Le verdict n'est repris que là où l'agrégat parle vraiment.
 */
/**
 * LA LIGNE DE FRAÎCHEUR D'UN SENS, lue de SA famille (lot 5d4, étendue au 5g).
 *
 * Sur un compteur à deux sens, les bornes ne sont pas les mêmes des deux côtés :
 * sur le C4 témoin de la maquette, le soutirage va au 08/09 et l'injection au
 * 04/09. Une ligne unique prenait la plus récente des deux et écrivait « à
 * jour » au-dessus d'une injection en retard de quatre jours.
 */
function ligneDeFraicheurDuSens(direction: DirectionEnedis | null) {
    const point = direction
        ? dernierPointFini(
              graphes.value
                  .flatMap((g) => g.series)
                  .filter((s) => s.direction === direction)
          )
        : dernierPointChargé.value
    const chargee = point ? (point.fin ?? point.debut) : null
    return ligneDeFraicheurDeLaFamille({
        onglet: ongletCourant.value,
        fraicheur: fraicheur.value,
        derniereChargee: chargee,
        debutDuContexte: props.contexte?.debut ?? null,
        finDeLaPeriodeConnue: point ? point.fin !== null : undefined,
        direction,
        // LE FLUX DE CETTE FAMILLE, quand dapi le sert : il porte l'attendu
        // NOMINAL et le verdict jugés sur ce flux-là (DEV-1845 tranche 2).
        statut: statut.value,
        aujourdHui: props.aujourdHui,
        // DE QUOI NOMMER LE FLUX qui date cette famille (S1 du 23/09) : le
        // segment décide du grain de la PMax, le pas du compteur nomme la
        // courbe. Le même contexte que le bloc de fraîcheur en tête de page,
        // pour que les deux écrivent le même mot du même flux.
        contexte: contexteDesFlux.value,
    })
}

const fraicheurDeLOnglet = computed(() => {
    const sens = directionsRegardees.value
    // UN SEUL SENS REGARDÉ : la ligne est celle d'avant, au mot près. Le sens
    // n'est pas écrit — l'onglet le dit déjà (« Énergie injectée »).
    if (sens.length < 2) return ligneDeFraicheurDuSens(sens[0] ?? null)
    // LES DEUX : chacun sa ligne, chacune nommée, comme la maquette
    // (« soutirage 08/09/2026 · à jour · injection 04/09/2026 · en retard »).
    return (
        sens
            .map((direction) => {
                const ligne = ligneDeFraicheurDuSens(direction)
                return ligne
                    ? `${LIBELLES_DIRECTION[direction].toLowerCase()} ${ligne}`
                    : null
            })
            .filter((ligne): ligne is string => ligne !== null)
            .join(" · ") || null
    )
})

/**
 * LA DERNIÈRE VALEUR RÉELLEMENT SERVIE pour la famille regardée (addendum 3).
 *
 * Elle se lit dans ce que l'onglet vient de charger, jamais dans une borne :
 * sur le C4 `638f1080`, `variables_datav2` borne la réactive au 15/09 et
 * `/series` rend 576 abscisses toutes nulles sur la même semaine. La borne
 * compte des documents, pas des mesures.
 */
const dernierPointChargé = computed(() =>
    dernierPointFini(graphes.value.flatMap((g) => g.series))
)

/**
 * Au grain `periode`, c'est la FIN de la dernière période qui date la famille :
 * la donnée d'un mois va jusqu'à son dernier jour, pas jusqu'à son premier.
 */
const derniereValeurChargee = computed(() => {
    const point = dernierPointChargé.value
    return point ? (point.fin ?? point.debut) : null
})

/**
 * La dernière valeur que la famille regardée DÉTIENT, en français. Elle n'est
 * écrite que par une carte qui n'a aucune valeur à montrer : elle dit alors que
 * ce n'est pas la fenêtre qui est en cause.
 */
const derniereValeurDeLaFamille = computed(() => {
    // Elle n'est servie à la carte QUE lorsqu'elle explique le vide : une borne
    // qui tombe dans le contexte ne dit pas jusqu'à quand la famille a servi,
    // elle dit seulement jusqu'où des documents existent (addendum 3).
    if (derniereValeurChargee.value) return null
    const jour = derniereValeurDeLaFamilleServie(ongletCourant.value)?.jour
    const debut = props.contexte?.debut
    return jour && debut && jour < debut ? jour : null
})

/**
 * LE RESPONSIVE ET LA DENSITÉ DE LA VUE (item 27c du brief v1.1, décision Q10).
 *
 * La maquette tient la vue d'un C5 entier en ~900 px là où la page en demandait
 * plus de 1 500 : une carte pleine largeur de 320 px par graphe, `gap-8`, un
 * pied de quatre lignes. À 1 366 × 768, la bande et la première figure n'étaient
 * JAMAIS visibles ensemble — donc jamais lisibles ensemble, alors que l'une
 * explique l'autre.
 *
 * Ce qui change (Q10, tranchée par le pilote le 15/09) : deux colonnes dès qu'un
 * panneau porte deux figures, 240 px par figure côte à côte, 300 px quand elle
 * est seule, `gap-4` entre les cartes, titre à `text-base`. Le mobile garde sa
 * colonne unique et sa figure resserrée (item 27c, inchangé).
 */
const { isMobile: estMobile } = useResponsive()

/**
 * ⚠️ LA LARGEUR ET LA HAUTEUR D'UNE FIGURE NE CHANGENT PLUS APRÈS SON PREMIER
 * TRACÉ, et ce n'est pas un raffinement : c'est un défaut mesuré.
 *
 * La première version de ce lot décidait des deux colonnes sur le NOMBRE DE
 * CARTES DÉJÀ LÀ. La carte journalière était donc peinte seule (une colonne,
 * 300 px), puis la carte de détail arrivait et la faisait passer à deux colonnes
 * et 240 px. Plotly redimensionne sur la boîte du conteneur ; pris dans ce
 * changement de hauteur, il recalculait la figure sur une boîte encore à zéro et
 * la laissait à `height: 0` — la carte principale disparaissait, mesuré dans
 * Chrome (vingt tests de navigateur rouges, dont les cinq de l'accessibilité).
 *
 * La grille se décide donc sur ce qu'on SAIT AVANT de peindre : la carte de
 * détail est-elle attendue sur cet onglet (fait connu dès la fenêtre regardée),
 * et l'onglet porte-t-il plusieurs grandeurs (elles arrivent toutes ensemble) ?
 */
const deuxColonnes = computed(
    () =>
        !estMobile.value &&
        (detailHoraireAttendu.value || graphes.value.length > 1)
)
const hauteurDeLaFigure = computed(() => {
    if (estMobile.value) return 240
    return deuxColonnes.value ? 240 : 300
})
const hauteurMinimaleDuContenu = computed(() =>
    estMobile.value ? "13rem" : "15rem"
)

/**
 * L'HEURE D'ATTEINTE DE CHAQUE POINT, TELLE QUE LE LECTEUR LA SERT (V6, §7.10).
 *
 * `meta.at` date la PMax : sans elle, une pointe est un nombre sans instant. Le
 * dépliant la reçoit d'ici parce qu'elle vit dans la META de la réponse, pas
 * dans la série tracée — et qu'aucune valeur n'est fabriquée au passage : les
 * réponses sont déjà recollées morceau par morceau par `concatenerMorceaux`,
 * donc alignées sur `x`.
 */
const heuresAtteinteDesSeries = computed<Record<string, Array<string | null>>>(
    () => {
        const par: Record<string, Array<string | null>> = {}
        for (const paquet of resultatCourant.value?.parDirection ?? []) {
            for (const reponse of paquet.reponses ?? []) {
                for (const [cle, heures] of Object.entries(
                    reponse?.meta?.at ?? {}
                )) {
                    const [variable, stat] = [
                        cle.slice(0, cle.lastIndexOf(".")),
                        cle.slice(cle.lastIndexOf(".") + 1),
                    ]
                    if (!variable || !stat) continue
                    par[
                        cleColonneAlignee({
                            direction: paquet.direction,
                            variable,
                            stat,
                        })
                    ] = heures
                }
            }
        }
        return par
    }
)

const grainTableau = computed<GrainTableau>(() =>
    grainDuTableau({
        jours: focus.value ? joursDeFenetre(focus.value) : 0,
        grainServi: grainServi.value,
        minutesPasNatif: minutesPasCompteur.value,
    })
)

// ============================================================================
// L'ÉNERGIE SE LIT AU JOUR DÈS QU'ON DÉPASSE DEUX SEMAINES (lot 5d, écart G4)
// ============================================================================
//
// La règle de résolution de la page (natif ≤ 14 j, horaire 15 à 92, journalier
// au-delà) vaut pour une COURBE : une puissance se lit à l'heure sur trois mois,
// et son enveloppe est ce qu'on vient chercher. Une ÉNERGIE, non : 2 208 barres
// horaires sur trois mois font un peigne d'un pixel où un trou de trois jours ne
// se distingue plus du trait entre deux barres. La maquette ne montre jamais
// cela : sa carte d'énergie est journalière dès qu'on dépasse la quinzaine, et
// le détail horaire vit à côté, sur les SEPT derniers jours mesurés.
//
// La règle n'est pas contournée, elle est APPLIQUÉE À CE QU'ON DEMANDE : on ne
// demande plus le pas fin pour une grandeur qui ne se lit pas ainsi. Le niveau
// SERVI reste celui de la réponse, et l'indicateur de Niveau écrit « journalier »
// sans la parenthèse de la règle, parce que ce n'est pas elle qui l'a choisi.
//
// Effet mesurable (E7/E8) : un contexte de 92 jours sur l'onglet Énergie passe
// de trois appels horaires (≈ 6 600 points) à un appel journalier (≈ 92 points)
// partagé avec la bande, plus un appel horaire de 168 points pour la carte de
// détail.

/**
 * Les grains DEMANDABLES pour cet onglet sur cette largeur. C'est la seule
 * entorse à « le niveau suit la fenêtre », et elle ne vaut que pour l'énergie.
 */
function grainsPourLOnglet(
    onglet: OngletResolu,
    fenetre: Fenetre | null
): typeof onglet.grains {
    const grains = onglet.grains ?? []
    if (onglet.cle !== "energie" || !fenetre) return grains
    if (joursDeFenetre(fenetre) <= SEUIL_NATIF_JOURS) return grains
    const auJour = grains.filter((g) => g !== "natif" && g !== "hourly")
    // Un compteur qui ne détiendrait QUE le pas fin garde ce qu'il a : mieux vaut
    // la seule résolution servie qu'un onglet vide.
    return auJour.length > 0 ? auJour : grains
}

/** Les statistiques demandées pour un onglet : celles que ses séries déclarent. */
function statsDeLOnglet(onglet: OngletResolu, direction: DirectionEnedis) {
    return [
        ...new Set(
            onglet.series
                .filter((s) => s.direction === direction)
                .flatMap((s) => s.stats)
        ),
    ]
}

/**
 * Charge ce qui manque à l'onglet regardé, et rien de plus : la mémoire du
 * composable rend zéro appel quand la fenêtre est déjà couverte.
 */
/**
 * Le numéro de la dernière demande de chargement. Un résultat qui revient alors
 * qu'une demande plus récente est partie n'a plus rien à dire : il écrirait à
 * l'écran la fenêtre d'avant.
 */
let derniereDemande = 0

async function chargerLOnglet(geste?: GesteRecadrage): Promise<void> {
    const onglet = ongletCourant.value
    const contexte = props.contexte
    const fenetre = focus.value
    if (!onglet || !contexte || !fenetre || !props.idSource) return

    const directions = directionsRegardees.value
    if (directions.length === 0) return

    const jeton = (derniereDemande += 1)
    const debut = Date.now()
    const avant = niveau.value
    // UNE DEMANDE PAR SENS (lot 5g), jamais l'union : sur un compteur à deux
    // sens, l'union envoyait à chacun les noms de l'autre et le lecteur
    // refusait l'appel entier.
    const variables = parSens(directions, (d) => variablesDeLOnglet(onglet, d))
    const stats = parSens(directions, (d) => statsDeLOnglet(onglet, d))

    let resultat: ResultatChargement
    try {
        resultat = await charger(
            {
                idSource: props.idSource,
                onglet: onglet.cle,
                directions,
                variables,
                stats,
                contexte,
                fenetre,
                grains: grainsPourLOnglet(onglet, fenetre),
            },
            undefined,
            // Une fenêtre découpée coûte plusieurs appels. Dès qu'un geste plus
            // récent est parti, les morceaux qui restent ne servent plus personne.
            () => jeton !== derniereDemande
        )
    } catch {
        // La lecture a ÉCHOUÉ. L'écran le dit avec ses mots à elle, jamais avec
        // ceux d'une grandeur non publiée.
        if (jeton === derniereDemande) {
            echecs.value = { ...echecs.value, [onglet.cle]: true }
        }
        return
    }
    // Doublé par une demande plus récente : ce résultat est périmé, il ne se
    // peint pas et ne se compte pas.
    if (jeton !== derniereDemande) return
    echecs.value = { ...echecs.value, [onglet.cle]: false }
    charges.value = { ...charges.value, [onglet.cle]: resultat }
    retenirLaCompletude(resultat)

    const duree = Date.now() - debut
    for (const direction of directions) {
        track({
            event: "Compteur Onglet Chargé",
            payload: {
                source_id: props.idSource,
                onglet: onglet.cle,
                direction,
                niveau: resultat.niveau,
                fenetre_jours: joursDeFenetre(fenetre),
                duree_ms: duree,
                nb_appels: resultat.appels,
                points_servis: compterLesPoints(resultat, direction),
                depuis_memoire: resultat.depuisMemoire,
            },
        })
    }

    // LE GESTE MIS EN ATTENTE SE JOUE MAINTENANT (écart T14). Le chargement est
    // fini, la page a la main : le dernier cliquer-glisser reçu pendant qu'elle
    // travaillait s'applique, au lieu d'être perdu.
    if (brushEnAttente && !enCours.value) {
        const attendu = brushEnAttente
        brushEnAttente = null
        if (!memeFenetre(attendu, focus.value)) {
            recadrer(attendu, "brush")
        }
    }

    if (geste) {
        track({
            event: "Compteur Vue Recadrée",
            payload: {
                source_id: props.idSource,
                geste,
                fenetre_avant: fenetreTracee(fenetreAvant.value),
                fenetre_apres: fenetreTracee(fenetre),
                niveau_avant: avant ?? undefined,
                niveau_apres: resultat.niveau,
                duree_ms: duree,
                depuis_memoire: resultat.depuisMemoire,
            },
        })
    }

    // LA CARTE DE DÉTAIL HORAIRE, UNE FOIS LA JOURNALIÈRE CHARGÉE : ses sept
    // jours se lisent dans les points qu'on vient de recevoir, pas dans un
    // calendrier. Son échec ne défait rien de ce qui précède.
    await chargerLeDetailHoraire(resultat)

    // LES PAS CHOISIS SURVIVENT AU RECADRAGE (R1). Chacun se rejoue sur la
    // nouvelle fenêtre, ou retombe sur le pas permis le plus proche en disant
    // pourquoi. Rejoué APRÈS l'onglet : la liste des cartes en dépend.
    await rejouerLesPasChoisis()
}

/**
 * Ce qui n'est pas servi sur cette fenêtre POUR CETTE SÉRIE. Le dépliant le
 * répète sous les chiffres : une colonne absente sans un mot se cherche.
 */
function mentionsDeLaSerie(graphe: GrapheEnedis, serie: SerieTracee): string[] {
    return [
        // Ce que la GRANDEUR est, quand son nom pourrait la faire prendre pour
        // une autre : le graphe le dit, les chiffres le disent aussi.
        ...(serie.mention ? [serie.mention] : []),
        ...graphe.plafonnees
            .filter(
                (m) =>
                    m.variable === serie.variable &&
                    m.direction === serie.direction
            )
            .map((m) => m.texte),
    ]
}

/**
 * Les mentions de TOUTES les séries du graphe, rangées par série : le dépliant
 * unique n'écrit que celles de la série qu'on regarde.
 */
function mentionsDuGraphe(graphe: GrapheEnedis): Record<string, string[]> {
    return Object.fromEntries(
        graphe.series.map((serie) => [
            serie.cle,
            mentionsDeLaSerie(graphe, serie),
        ])
    )
}

/** Combien de points ont été servis : un compte, jamais une valeur. */
function compterLesPoints(
    resultat: ResultatChargement,
    direction: DirectionEnedis
): number {
    const reponses: ReponseSeries[] =
        resultat.parDirection.find((p) => p.direction === direction)
            ?.reponses ?? []
    return reponses.reduce(
        (total, reponse) =>
            total +
            (reponse.data ?? []).reduce((n, serie) => n + serie.x.length, 0),
        0
    )
}

// ============================================================================
// LES GESTES
// ============================================================================

const fenetreAvant = ref<Fenetre | null>(null)

/**
 * LE DERNIER CLIQUER-GLISSER REÇU PENDANT UN CHARGEMENT (écart T14).
 *
 * Il ne se joue pas tout de suite — une rafale relancerait un chargement à
 * chaque battement — mais il n'est PAS jeté : il attend la fin du chargement en
 * cours, et c'est le dernier reçu qui gagne. `null` = rien n'attend.
 */
let brushEnAttente: Fenetre | null = null

/**
 * LE CLIC SUR L'ONGLET DÉJÀ REGARDÉ.
 *
 * `Tabs.updateValue` (`node_modules/primevue/tabs/index.mjs` ligne 59) garde
 * sur l'inégalité : cliquer l'onglet actif n'émet AUCUN `update:value`. Or
 * c'est exactement le clic mort que le détecteur de friction doit voir
 * (`SourceDataEnedisView.suivi.spec.ts`, « déclare une rage de clics une fois,
 * pas trois »). Ce handler-là le rattrape, et lui seul : un clic qui change
 * d'onglet passe par `update:value`, jamais deux fois.
 */
function clicSurLOngletRegarde(cle: CleOnglet): void {
    if (cle === ongletActif.value) ouvrir(cle)
}

function ouvrir(cle: CleOnglet): void {
    const dejaLa = ongletActif.value === cle
    detecteur.clic(`onglet:${cle}`, Date.now(), !dejaLa)
    if (dejaLa) return
    ongletActif.value = cle
    ouverts.value = new Set([...ouverts.value, cle])
    void chargerLOnglet()
}

function choisirLeSens(cle: FiltreDirection): void {
    const avant = filtre.value
    detecteur.clic(`direction:${cle}`, Date.now(), avant !== cle)
    if (avant === cle) return
    filtre.value = cle
    if (props.idSource) {
        track({
            event: "Compteur Direction Changée",
            payload: {
                source_id: props.idSource,
                direction_avant: avant,
                direction_apres: cle,
            },
        })
    }
    void chargerLOnglet()
}

/** Un recadrage : le geste devient une fenêtre, bornée par le contexte. */
function recadrer(fenetre: Fenetre | null, geste: GesteRecadrage): void {
    if (!props.contexte) return
    // UN GESTE N'EST JAMAIS PERDU (DEV-1508 lot 2d). Cette ligne refusait tout
    // recadrage tant qu'un chargement était en vol. Ce qui se voyait dans
    // Chrome, sur le récit témoin : « Revenir au contexte » cliqué dans la
    // demi-seconde qui suit un cliquer-glisser ne faisait RIEN — la page restait
    // sur la fenêtre du brush et y restait pour de bon. La main de l'utilisateur
    // était donc en retard d'un geste sur ce que l'écran montrait.
    //
    // Ce que la garde protégeait reste tenu, mais LE GESTE N'EST PLUS JETÉ
    // (lot 5c, écart T14). Un `brush` reçu pendant un chargement est MIS EN
    // ATTENTE et joué à la fin : une rafale ne relance donc toujours rien (le
    // danger de boucle vient de là), mais le dernier cliquer-glisser de la main
    // finit par s'appliquer. Depuis que le journalier part découpé par 92 jours
    // (T13), un chargement dure quatre appels : jeter ce qui tombe pendant ce
    // temps-là serait devenu la règle plutôt que l'exception.
    if (enCours.value && geste === "brush") {
        brushEnAttente = fenetre
        return
    }
    // Un geste de la main ANNULE le brush en attente : sans cela, « Revenir au
    // contexte » cliqué juste après un cliquer-glisser serait défait, une
    // seconde plus tard, par le glisser qui dormait encore.
    brushEnAttente = null
    if (!fenetre) {
        if (props.idSource) {
            track({
                event: "Compteur Vue Recadrée",
                payload: {
                    source_id: props.idSource,
                    geste,
                    fenetre_avant: fenetreTracee(focus.value),
                    fenetre_apres: null,
                    avorte: true,
                },
            })
        }
        return
    }
    if (memeFenetre(fenetre, focus.value)) return
    fenetreAvant.value = focus.value
    focus.value = fenetre
    void chargerLOnglet(geste)
}

function surBrushDuGraphe(bornes: { debut: string; fin: string }): void {
    if (!props.contexte) return
    recadrer(fenetreDuBrush(bornes.debut, bornes.fin, props.contexte), "brush")
}

function surBrushDeLaBande(bornes: { debut: number; fin: number }): void {
    const jours = (bandeDeCompletude.value?.jours ?? []).map((j) => j.jour)
    recadrer(fenetreDeLaBande(bornes.debut, bornes.fin, jours), "bande")
}

/**
 * LA FENÊTRE DÉPLACÉE À LA MAIN SUR LA BANDE (lot 5e, écart T9).
 *
 * La bande a déjà conservé la largeur et respecté les bornes du contexte : elle
 * remonte deux index, comme pour un choix. Le geste porte un AUTRE nom parce
 * qu'il est fait de la main, et qu'il ne se jette jamais pendant un chargement
 * (`recadrer` ne met en attente que les `brush`, que Plotly peut fabriquer tout
 * seul en recalculant ses axes).
 */
function surDeplacementDeLaBande(bornes: { debut: number; fin: number }): void {
    const jours = (bandeDeCompletude.value?.jours ?? []).map((j) => j.jour)
    recadrer(
        fenetreDeLaBande(bornes.debut, bornes.fin, jours),
        "bande_deplacer"
    )
}

// « ALLER AU JOUR DU PIC » A ÉTÉ RETIRÉ (revue du 17/09, point R3 : « Pour la
// puissance, supprimer le "Aller au jour du pic" »). Le bouton, son jour lu, son
// geste de recadrage `pic` et son suivi de clic sont partis avec lui : le
// cliquer-glisser reste le seul chemin pour resserrer sur une journée.

function revenirAuContexte(geste: GesteRecadrage): void {
    detecteur.clic("bouton:revenir-au-contexte", Date.now(), !auContexte.value)
    if (auContexte.value) return
    recadrer(props.contexte, geste)
}

/**
 * ÉCHAP RAMÈNE AU CONTEXTE (DEV-1508 lot T10, maquette v1.5).
 *
 * Même chemin que le bouton : `recadrer` vers `props.contexte`, rien de plus
 * en réseau que ce qui est déjà en mémoire. Deux gardes avant d'agir :
 *  - un champ de saisie ou un sélecteur ouvert garde la touche pour lui (les
 *    deux champs de dates, notamment) ;
 *  - un geste en cours sur la bande garde la touche pour lui : son propre
 *    Échap (lot 5e) appelle `preventDefault`, qu'on lit ici pour ne pas agir
 *    deux fois sur le même appui.
 */
function surEchapGlobal(evenement: KeyboardEvent): void {
    if (evenement.key !== "Escape" || evenement.defaultPrevented) return
    const cible = evenement.target as HTMLElement | null
    const dansUnChamp =
        !!cible &&
        (["INPUT", "SELECT", "TEXTAREA"].includes(cible.tagName) ||
            cible.isContentEditable)
    if (dansUnChamp) return
    revenirAuContexte("echap")
}

// ============================================================================
// LE RECADRAGE AU CLAVIER : DEUX CHAMPS DE DATES (DEV-1508 lot B5)
// ============================================================================
//
// Jusqu'ici, RESSERRER la fenêtre était un geste de souris et rien d'autre :
// cliquer-glisser sur un graphe ou sur la bande. Seul le RETOUR était
// atteignable au clavier, par le bouton. Un client qui navigue au clavier, ou
// à la voix, ou avec un lecteur d'écran, pouvait donc annuler un recadrage
// qu'il n'avait aucun moyen de faire. Ces deux champs lèvent la limite.
//
// Ils ne sont PAS un second chemin de recadrage : ils appellent `recadrer`,
// exactement comme le brush et comme la bande. Même focus, même événement de
// suivi (« Compteur Vue Recadrée », seul le `geste` change), même chargement
// donc mêmes appels réseau selon le grain. Tout ce qui suit la fenêtre — la
// bande, les graphes, les totaux, le tableau — suit de la même façon.
//
// Le `geste` s'appelle `dates` et non `brush`, pour deux raisons qui comptent :
// le suivi doit pouvoir dire QUI se sert du clavier, et surtout `recadrer`
// laisse tomber un `brush` pendant un chargement (Plotly peut en fabriquer
// tout seul en recalculant ses axes). Une saisie vient forcément de la main :
// elle est tenue, chargement en vol ou non, comme le bouton et la bande.

/** Un jour tel que le champ natif le rend : `AAAA-MM-JJ`. */
const JOUR_ISO = /^\d{4}-\d{2}-\d{2}$/

const dateDu = ref("")
const dateAu = ref("")

/**
 * La règle qu'une saisie refusée vient d'enfreindre, écrite DANS la barre —
 * jamais une bannière, jamais une notification volante (règle du 08/09).
 * `null` quand rien n'a été refusé.
 */
const regleDesDates = ref<string | null>(null)

/**
 * Les champs disent TOUJOURS la fenêtre regardée, d'où qu'elle vienne : un
 * cliquer-glisser sur un graphe, un glisser sur la bande, le bouton de retour.
 * C'est cette ligne qui fait que « Revenir au contexte » remet les champs au
 * contexte, sans que le bouton ait à les connaître.
 *
 * `flush: "sync"` n'est pas un ornement. Un observateur ordinaire est différé
 * au tour suivant : un recadrage accepté sur le premier champ effacerait alors
 * la règle affichée par le refus du SECOND, après coup, et l'écran resterait
 * muet sur la borne qu'il vient de refuser. La remise à zéro doit se faire au
 * moment du recadrage, pas plus tard.
 */
watch(
    focus,
    (f) => {
        dateDu.value = f?.debut ?? ""
        dateAu.value = f?.fin ?? ""
        regleDesDates.value = null
    },
    { immediate: true, flush: "sync" }
)

/**
 * Une saisie validée devient la fenêtre regardée ; une saisie refusée ne
 * change rien et dit pourquoi.
 *
 * QUAND on valide, et pourquoi pas au `change` : un champ de date natif émet
 * un `change` à CHAQUE état complet intermédiaire pendant qu'on tape l'année.
 * Taper « 2026 » fait passer le champ par les années 0002, 0020 puis 0202,
 * chacune un `change` et chacune hors du contexte — la fenêtre se ferait
 * refuser trois fois, et le champ serait récrit sous les doigts de celui qui
 * tape. On valide donc quand la saisie est FINIE : au départ du champ, ou sur
 * Entrée. Mesuré dans Chrome le 14/09 sur le récit témoin ; c'est ce qui a
 * rendu la première version de ce lot rouge.
 *
 * Un refus rend aux DEUX champs les bornes réellement tracées : la barre ne
 * garde jamais affichée une date qu'aucun graphe ne trace. Rendre au seul
 * champ quitté sa valeur laisserait l'autre montrer la borne impossible, et
 * l'écran raconterait une fenêtre qui n'existe pas.
 *
 * `min` et `max` sont posés sur le contexte dans le gabarit, mais un champ de
 * date natif accepte quand même une date hors bornes tapée au clavier : la
 * règle se tient donc ici, pas dans l'attribut.
 */
function appliquerLesDates(): void {
    const contexte = props.contexte
    if (!contexte) return
    const du = dateDu.value
    const au = dateAu.value
    const refuser = (regle: string): void => {
        dateDu.value = focus.value?.debut ?? ""
        dateAu.value = focus.value?.fin ?? ""
        regleDesDates.value = regle
    }
    if (!JOUR_ISO.test(du) || !JOUR_ISO.test(au)) {
        return refuser("Il faut deux dates, au format jj/mm/aaaa.")
    }
    if (du > au) {
        return refuser("La date de début ne peut pas suivre la date de fin.")
    }
    if (du < contexte.debut || au > contexte.fin) {
        return refuser(
            `Les dates tiennent entre le ${dateFr(contexte.debut)} et le ${dateFr(contexte.fin)}.`
        )
    }
    regleDesDates.value = null
    recadrer({ debut: du, fin: au }, "dates")
}

function surValeursDepliees(
    onglet: CleOnglet,
    detail: { grain: GrainTableau; lignes: number; serie: string }
): void {
    if (!props.idSource) return
    track({
        event: "Compteur Valeurs Dépliées",
        payload: {
            source_id: props.idSource,
            onglet,
            grain_tableau: detail.grain,
            lignes: detail.lignes,
            // Quelle courbe a été ouverte : depuis le lot B2 le dépliant est
            // unique et le choix de la série est le geste qui compte.
            serie: detail.serie,
        },
    })
}

// ============================================================================
// LE SUIVI
// ============================================================================

onMounted(() => {
    window.addEventListener("keydown", surEchapGlobal)
    demarrerReplaySurLaRoute()
    if (props.idSource) {
        track({
            event: "Compteur Page Opened",
            payload: {
                source_id: props.idSource,
                vue: "v2",
                entree: props.entree,
                segment: props.segment,
                segment_injection: props.directions.includes("injection"),
                directions: props.directions,
                contexte_jours: props.contexte
                    ? joursDeFenetre(props.contexte)
                    : undefined,
                onglet_initial: ongletActif.value ?? undefined,
                // À l'ouverture, la bande n'est pas encore calculée : les
                // séries ne sont pas chargées et le verdict pas encore là. Le
                // champ est donc absent, et c'est plus juste qu'un instantané
                // pris sur une complétude d'ailleurs.
                completude: bandeDeCompletude.value
                    ? {
                          mesure: bandeDeCompletude.value.parEtat.mesure,
                          collecte: bandeDeCompletude.value.parEtat.homeys,
                          enedis: bandeDeCompletude.value.parEtat.enedis,
                          manquant: bandeDeCompletude.value.parEtat.manquant,
                      }
                    : undefined,
                flux_en_retard: Array.isArray(props.verdict?.streams_en_retard)
                    ? props.verdict.streams_en_retard.length
                    : undefined,
            },
        })
    }
    // LA BANDE D'ABORD, L'ONGLET ENSUITE, et c'est ce qui rend l'appel UNIQUE :
    // au contexte, l'onglet Énergie demande exactement ce que la bande vient de
    // retenir sous la même clé de mémoire. L'inverse (deux départs simultanés)
    // ferait partir deux appels identiques, aucun des deux n'ayant encore rien à
    // lire en mémoire.
    void chargerLaBande().then(() => chargerLOnglet())
})

onUnmounted(() => {
    window.removeEventListener("keydown", surEchapGlobal)
    detecteur.reinitialiser()
    // L'anti-rebond du contexte ne survit pas à la vue : un chargement réveillé
    // après le démontage écrirait dans un écran qui n'existe plus.
    if (minuterieDuContexte) clearTimeout(minuterieDuContexte)
    minuterieDuContexte = null
    brushEnAttente = null
})
</script>

<template>
    <section
        class="source-data-enedis flex flex-col gap-5"
        data-testid="source-data-enedis"
        :data-entree="entree"
    >
        <!-- ============================================================ -->
        <!-- LA CARTE DU COMPTEUR : les huit faits, et la fraîcheur.         -->
        <!-- ============================================================ -->
        <BaseTitledCard
            data-testid="carte-compteur"
            header-class="py-4! flex items-center justify-between gap-4"
            content-class="p-5"
        >
            <template #header>
                <h2
                    class="m-0 text-lg font-semibold"
                    data-testid="titre-compteur"
                >
                    {{ titreDeLaCarte }}
                </h2>
                <span
                    v-if="prm"
                    class="font-mono text-sm font-normal text-color"
                    data-testid="numero-du-point"
                    >{{ prm }}</span
                >
            </template>

            <template #content>
                <div
                    class="grid gap-x-8 gap-y-4 md:grid-cols-[1.3fr_1fr] md:items-start"
                >
                    <!-- Les faits servis, en grille clé/valeur du système
                         (item 12 du brief de design, question Q12). Un fait non
                         connu ne s'affiche pas, il ne s'écrit pas « — » : un
                         tiret se lirait comme une absence constatée. -->
                    <div class="flex min-w-0 flex-col gap-3">
                        <dl
                            class="m-0 grid grid-cols-[repeat(auto-fill,minmax(11rem,1fr))] gap-x-6 gap-y-3"
                            data-testid="entete"
                        >
                            <div
                                v-for="fait in faits"
                                :key="fait.cle"
                                class="flex min-w-0 flex-col gap-0.5"
                                :data-fait="fait.cle"
                            >
                                <dt
                                    class="text-xs tracking-wide text-primary-600 uppercase"
                                >
                                    {{ fait.libelle }}
                                </dt>
                                <dd
                                    class="m-0 text-sm font-semibold text-primary-800 [overflow-wrap:anywhere]"
                                    :class="fait.mono ? 'font-mono' : ''"
                                >
                                    {{ fait.valeur }}
                                </dd>
                            </div>
                        </dl>

                        <!-- LA CARTE DU COMPTEUR (écart E4) : tout ce que cette
                             grille ne porte pas — identité, contrat, technique,
                             consentement — vit déjà sur la page d'informations
                             de la source. Un lien vers une route RÉELLE, jamais
                             une ancre morte (question Q11). -->
                        <RouterLink
                            v-if="idSource"
                            class="text-sm text-primary no-underline hover:underline"
                            data-testid="lien-carte-compteur"
                            :to="{
                                name: 'PageSourceInfoGenerale',
                                params: { _id_source: idSource },
                            }"
                        >
                            Carte du compteur : identité, contrat, technique,
                            consentement →
                        </RouterLink>
                    </div>

                    <!-- La fraîcheur, lue du verdict ou du statut servi. Rien
                         quand ni l'un ni l'autre ne la dit : un compteur dont on
                         ne sait rien n'est pas dit « à jour ». Rien non plus
                         quand Enedis sert un état (résilié, inexistant) : c'est
                         le titre qui le porte, et deux états ne se contredisent
                         pas sur le même écran. Le rond est DÉCORATIF :
                         l'information est dans la phrase, jamais dans la
                         couleur seule. -->
                    <div v-if="fraicheur" class="flex flex-col gap-1.5">
                        <h3
                            class="m-0 text-xs tracking-wide text-primary-600 uppercase"
                        >
                            Fraîcheur, sur l'attendu de ce compteur
                        </h3>
                        <ul
                            class="divide-surface-200 m-0 flex list-none flex-col divide-y divide-dashed p-0 text-sm text-color"
                            :data-a-jour="fraicheur.aJour"
                            data-testid="fraicheur"
                        >
                            <li
                                v-for="ligne in fraicheur.lignes"
                                :key="ligne.cle"
                                class="flex items-baseline gap-2 py-1"
                            >
                                <span
                                    class="source-data-enedis__point h-2.5 w-2.5 flex-none translate-y-px rounded-full"
                                    :class="`source-data-enedis__point--${ligne.etat ?? classeDuPoint(fraicheur)}`"
                                    aria-hidden="true"
                                ></span>
                                <span>{{ ligne.texte }}</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </template>
        </BaseTitledCard>

        <!-- ============================================================ -->
        <!-- LE CONTEXTE : le sens regardé, la fenêtre, la bande.            -->
        <!-- La bande et la barre restent HORS du système de cartes : ce sont -->
        <!-- deux objets de contexte qui commandent les cartes en dessous,    -->
        <!-- pas des contenus de même rang (item 38 du brief v1.1).          -->
        <!-- ============================================================ -->
        <div class="flex flex-col gap-3">
            <!-- LA BARRE DE CONTEXTE (lot 5c, écarts B3, T1).
                 Le contexte est la période CHARGÉE : les raccourcis la
                 recalculent, la fin restant au dernier jour dû. Ce ne sont pas
                 des fenêtres préréglées de la fenêtre regardée — celle-là se
                 resserre toujours au cliquer-glisser ou aux deux champs. -->
            <div
                v-if="contexteLisible"
                class="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-color"
                data-testid="barre-contexte"
            >
                <span data-testid="contexte">
                    Contexte
                    <b class="font-semibold">{{ contexteLisible }}</b>
                </span>
                <BaseSelectButton
                    :model-value="joursContexte"
                    :options="RACCOURCIS_DE_CONTEXTE"
                    option-label="libelle"
                    option-value="jours"
                    :allow-empty="false"
                    size="small"
                    aria-label="Durée du contexte"
                    data-testid="raccourcis-contexte"
                    @update:model-value="choisirLeContexte"
                >
                    <template #option="{ option }">
                        <span :data-raccourci="option.jours">{{
                            option.libelle
                        }}</span>
                    </template>
                </BaseSelectButton>
            </div>

            <div
                v-if="choixDirection.length > 0 || fenetreLisible"
                class="flex flex-wrap items-center gap-x-6 gap-y-2"
            >
                <!-- Le sens regardé, seulement quand le compteur en porte
                     deux. Même bascule que le Tableau / Graphe de la page
                     GRDF. -->
                <BaseSelectButton
                    v-if="choixDirection.length > 0"
                    :model-value="filtre"
                    :options="choixDirection"
                    option-label="libelle"
                    option-value="cle"
                    :allow-empty="false"
                    size="small"
                    aria-label="Sens regardé"
                    data-testid="directions"
                    @update:model-value="choisirLeSens"
                >
                    <template #option="{ option }">
                        <span :data-direction="option.cle">{{
                            option.libelle
                        }}</span>
                    </template>
                </BaseSelectButton>

                <!-- Sans sélecteur de sens, la phrase de la fenêtre commence au
                     bord gauche, alignée avec la carte au dessus et la bande en
                     dessous : pas de trou à la place du sélecteur (item 28). -->
                <div
                    v-if="fenetreLisible"
                    class="flex flex-wrap items-center gap-3 text-sm text-color"
                    :class="{ 'ms-auto': choixDirection.length > 0 }"
                    data-testid="fenetre-regardee"
                >
                    <span>
                        Fenêtre regardée :
                        <b class="font-semibold">{{ fenetreLisible }}</b>
                        <template v-if="auContexte">
                            (tout le contexte)</template
                        >
                        <!-- Le niveau servi, là où il s'explique : à côté de la
                             largeur qui le décide (écart T4, décision Q8). -->
                        <template v-if="libelleNiveau">
                            · niveau
                            <span data-testid="niveau">{{
                                libelleNiveau
                            }}</span>
                        </template>
                    </span>
                    <!-- L'aide dit LES DEUX gestes de la bande (écart T9). Le
                         « puis » n'est pas de la politesse : tant qu'on est au
                         contexte, il n'y a pas encore de fenêtre à déplacer, et
                         une aide qui inviterait à un geste sans effet
                         apprendrait quelque chose de faux. -->
                    <span v-if="auContexte">
                        Cliquer-glisser sur un graphe ou sur la bande pour
                        resserrer ; puis déplacer la fenêtre à la main sur la
                        bande.
                    </span>

                    <!-- Le recadrage au clavier (lot B5). Deux champs natifs,
                         libellés statiques : un champ de date n'est jamais
                         vide au sens du navigateur, un libellé flottant y
                         resterait en permanence en position haute (item 31).
                         Ils sont ici, après le sens regardé et avant la bande,
                         parce que c'est là que la tabulation doit les
                         rencontrer. -->
                    <span
                        v-if="contexte"
                        class="inline-flex flex-wrap items-center gap-2"
                    >
                        <label
                            for="fenetre-du"
                            class="text-xs font-medium text-color"
                        >
                            Du
                        </label>
                        <BaseInputText
                            id="fenetre-du"
                            v-model="dateDu"
                            type="date"
                            size="small"
                            :min="contexte.debut"
                            :max="contexte.fin"
                            :aria-describedby="
                                regleDesDates ? 'fenetre-regle' : undefined
                            "
                            data-testid="fenetre-du"
                            @blur="appliquerLesDates"
                            @keyup.enter="appliquerLesDates"
                        />
                        <label
                            for="fenetre-au"
                            class="text-xs font-medium text-color"
                        >
                            Au
                        </label>
                        <BaseInputText
                            id="fenetre-au"
                            v-model="dateAu"
                            type="date"
                            size="small"
                            :min="contexte.debut"
                            :max="contexte.fin"
                            :aria-describedby="
                                regleDesDates ? 'fenetre-regle' : undefined
                            "
                            data-testid="fenetre-au"
                            @blur="appliquerLesDates"
                            @keyup.enter="appliquerLesDates"
                        />
                    </span>
                    <span
                        v-if="enCours"
                        class="inline-flex items-center gap-2"
                        role="status"
                        aria-live="polite"
                        data-testid="chargement"
                    >
                        <i class="pi pi-spin pi-spinner" aria-hidden="true" />
                        Chargement
                    </span>
                    <BaseButton
                        v-if="!auContexte"
                        outlined
                        size="small"
                        icon="pi pi-refresh"
                        label="Revenir au contexte (double-clic ou Échap)"
                        data-testid="revenir-au-contexte"
                        @click="revenirAuContexte('bouton')"
                    />

                    <!-- La règle qu'une saisie refusée vient d'enfreindre.
                         Une phrase dans la barre, à la ligne sous les champs :
                         ni bannière, ni notification volante (règle du 08/09). -->
                    <span
                        v-if="regleDesDates"
                        id="fenetre-regle"
                        class="w-full text-xs text-color"
                        role="status"
                        aria-live="polite"
                        data-testid="regle-des-dates"
                    >
                        {{ regleDesDates }}
                    </span>
                </div>
            </div>

            <!-- La bande de complétude : les trous, à leur date, quatre mots.
                 On y glisse pour recadrer, comme sur un graphe. -->
            <!-- LE COMPTE CUMULÉ DES DEUX SENS (lot 5g) : des JOURS mesurés,
                 jamais des énergies — les deux sens ne se somment pas (E5). -->
            <p
                v-if="compteCumuleDesDeuxSens"
                class="text-color m-0 text-xs"
                data-testid="completude-cumulee"
            >
                {{ compteCumuleDesDeuxSens }}
            </p>
            <DataCompletudeBar
                v-for="bande in bandesDeCompletude"
                :key="bande.direction"
                :completude="bande.completude"
                :titre="bande.titre"
                :data-direction-bande="bande.direction"
                :mots-contrastes="true"
                :fenetre="indexDeLaFenetre"
                @brush="surBrushDeLaBande"
                @deplacer="surDeplacementDeLaBande"
                @retour="revenirAuContexte('double_clic')"
            />
            <!-- L'emplacement de la bande, tenu et MUET tant que le verdict
                 n'est pas arrivé : aucun mot d'état, donc aucun mot à démentir.
                 Le squelette REPREND LA COQUE de la bande (mêmes bord, rayon,
                 marges, hauteurs) plutôt qu'une hauteur devinée : la mise en
                 page ne saute pas quand la vraie bande arrive. -->
            <div
                v-if="bandesDeCompletude.length === 0 && bandeEnAttente"
                class="border-surface-200 flex flex-col gap-2 rounded-md border border-solid bg-white px-4 py-3"
                data-testid="completude-en-attente"
                role="status"
                aria-busy="true"
                aria-label="Complétude du contexte, chargement en cours"
            >
                <!-- Les deux lignes de texte de la bande occupent 19,5 px et
                     16,8 px, mesurés dans le navigateur : le squelette leur
                     rend leur boîte exacte, sinon l'emplacement gagnait 11 px
                     quand le verdict arrivait, et la page sautait. -->
                <div class="flex h-[19.5px] items-center">
                    <BaseSkeleton
                        width="14rem"
                        height="0.8125rem"
                        aria-hidden="true"
                    />
                </div>
                <BaseSkeleton
                    width="100%"
                    height="18px"
                    border-radius="3px"
                    aria-hidden="true"
                />
                <div class="flex h-[16.8px] items-center">
                    <BaseSkeleton
                        width="10rem"
                        height="0.75rem"
                        aria-hidden="true"
                    />
                </div>
            </div>
        </div>

        <!-- ============================================================ -->
        <!-- LES ONGLETS, et le panneau de l'onglet regardé.                 -->
        <!-- `lazy` : un SEUL panneau vit dans le DOM, celui qu'on regarde.  -->
        <!-- C'est le `v-if` d'hier, mot pour mot, et c'est ce qui tient E1  -->
        <!-- et E8 — sans lui, les cinq panneaux se monteraient et           -->
        <!-- déclencheraient cinq chargements.                              -->
        <!-- ============================================================ -->
        <div class="flex flex-col gap-4">
            <BaseTabs
                id="onglets-enedis"
                lazy
                :show-navigators="false"
                :value="ongletActif ?? undefined"
                @update:value="ouvrir($event as CleOnglet)"
            >
                <!-- Le nom de la liste se pose sur l'élément qui porte le
                     rôle de liste d'onglets, pas sur la coque : posé sur la
                     coque, il ne nomme rien, et axe-core le relève. -->
                <BaseTabList
                    data-testid="onglets"
                    :pt="{
                        tabList: {
                            class: 'flex-wrap',
                            'aria-label': 'Catégories de données',
                        },
                    }"
                >
                    <!-- Au repos, le préréglage peint un onglet en gris
                         estompé (#9e9e9e, 2,67:1 sur blanc) : sous le seuil
                         AA. Le module le tient au texte courant (#616161,
                         6,19:1), le rang restant porté par la graisse. -->
                    <BaseTab
                        v-for="onglet in onglets"
                        :key="onglet.cle"
                        :value="onglet.cle"
                        :data-onglet="onglet.cle"
                        :pt="{
                            root:
                                onglet.cle === ongletActif
                                    ? 'font-semibold'
                                    : 'text-color!',
                        }"
                        @click="clicSurLOngletRegarde(onglet.cle)"
                    >
                        {{ onglet.libelle }}
                    </BaseTab>
                </BaseTabList>

                <BaseTabPanels :pt="{ root: 'p-0!' }">
                    <BaseTabPanel
                        v-for="onglet in onglets"
                        :key="onglet.cle"
                        :value="onglet.cle"
                        data-testid="panneau"
                        :data-panneau="onglet.cle"
                        class="flex flex-col gap-4 p-0!"
                    >
                        <template
                            v-if="
                                ongletCourant &&
                                onglet.cle === ongletCourant.cle &&
                                ouverts.has(onglet.cle)
                            "
                        >
                            <!-- CE QUE CET ONGLET A REÇU, EN TÊTE DE SON
                                 PANNEAU (écarts G1 et G2). Un nombre et une
                                 date, jamais un badge d'alerte ni une
                                 bannière : la bande est en haut de l'écran, la
                                 courbe est ici, et entre les deux il y a un
                                 écran de défilement. -->
                            <!-- S9 de la review du 23/09 : « la mise en forme
                                 est moins bien que le reste ». Les deux faits
                                 étaient posés dans une grille à `gap-x-6`, et
                                 l'espace de six unités entre « 17 / 17 jours
                                 mesurés » et « dernière valeur le … » ne
                                 ressemblait à aucune autre ligne de la page —
                                 qui sépare toutes ses énumérations par un
                                 point médian. Un seul paragraphe, un seul
                                 séparateur, la même graisse : le rappel reste
                                 le sujet de la phrase, la fraîcheur en est la
                                 suite. -->
                            <p
                                v-if="rappelDeLOnglet || fraicheurDeLOnglet"
                                class="text-color m-0 flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm"
                            >
                                <span
                                    v-if="rappelDeLOnglet"
                                    data-testid="rappel-onglet"
                                >
                                    {{ rappelDeLOnglet }}
                                </span>
                                <span
                                    v-if="rappelDeLOnglet && fraicheurDeLOnglet"
                                    aria-hidden="true"
                                    >·</span
                                >
                                <span
                                    v-if="fraicheurDeLOnglet"
                                    data-testid="fraicheur-onglet"
                                >
                                    {{ fraicheurDeLOnglet }}
                                </span>
                            </p>

                            <!-- LA GRILLE DES FIGURES (décision Q10).
                                 DEUX PISTES FIXES, posées avant que la
                                 première figure ne soit peinte, et non un
                                 `auto-fit` qui n'en ouvrirait une seconde qu'à
                                 l'arrivée de la carte de détail : cette
                                 seconde piste-là redimensionnerait une figure
                                 déjà tracée, et Plotly la laissait à hauteur
                                 zéro (mesuré dans Chrome). Sous 768 px, la
                                 grille retombe à une colonne par
                                 `deuxColonnes`, comme le reste de la page.

                                 ⚠️ La piste UNIQUE est écrite elle aussi, en
                                 `minmax(0, 1fr)` : une grille sans colonnes
                                 déclarées dimensionne sa piste implicite sur le
                                 CONTENU, et le tableau des valeurs, plus large
                                 que 375 px, poussait alors la carte entière
                                 hors de l'écran (89 px de débordement mesurés
                                 dans Chrome). Le `minmax(0, …)` la ramène à la
                                 place disponible, et le tableau redevient le
                                 seul objet qui défile, DANS sa carte. -->
                            <div
                                class="grid gap-4"
                                :style="
                                    deuxColonnes
                                        ? 'grid-template-columns: repeat(2, minmax(0, 1fr))'
                                        : 'grid-template-columns: minmax(0, 1fr)'
                                "
                                data-testid="grille-des-graphes"
                            >
                                <GraphiqueSeriesEnedis
                                    v-for="graphe in cartesDuPanneau"
                                    :key="graphe.cle"
                                    :graphe="graphe"
                                    :rendu="
                                        estLaCarteDeDetail(graphe)
                                            ? 'courbe'
                                            : renduDeLOnglet(ongletCourant.cle)
                                    "
                                    :fenetre="fenetreDeLaCarte(graphe)"
                                    :contexte="contexte"
                                    :grain="grainDeLaCarte(graphe)"
                                    :cles-de-l-onglet="
                                        ongletCourant.series.map((s) => s.cle)
                                    "
                                    :prm="prm"
                                    :cle-onglet-lisible="ongletCourant.libelle"
                                    :chargement="enCours"
                                    :echec-de-lecture="echecDeLecture"
                                    :sommable="
                                        !estLaCarteDeDetail(graphe) &&
                                        estSommableSurLaFenetre(
                                            ongletCourant.cle,
                                            graphe.unite
                                        )
                                    "
                                    :hauteur="hauteurDeLaFigure"
                                    :hauteur-minimale="hauteurMinimaleDuContenu"
                                    :jours-absents="
                                        joursAbsentsDeLaCarte(graphe)
                                    "
                                    :contexte-non-detaillable="
                                        contexteNonDetaillable
                                    "
                                    :niveau-lisible="
                                        porteLeNiveau(graphe)
                                            ? libelleNiveauCourt
                                            : null
                                    "
                                    :axe-aux-valeurs="
                                        axeYAuxValeurs(ongletCourant.cle)
                                    "
                                    :derniere-valeur-de-la-famille="
                                        derniereValeurDeLaFamille
                                    "
                                    :derniere-valeur-de-l-onglet="
                                        derniereValeurChargee
                                    "
                                    :total-des-classes="
                                        ongletCourant.cle === 'depassements'
                                    "
                                    :minutes-pas-compteur="minutesPasCompteur"
                                    :pas-permis="pasPermisDeLaCarte(graphe)"
                                    :pas-servi="pasServiDeLaCarte(graphe)"
                                    :raison-du-pas="
                                        raisonDuPasDeLaCarte(graphe)
                                    "
                                    @brush="surBrushDuGraphe"
                                    @retour="revenirAuContexte('double_clic')"
                                    @pas="choisirLePas(graphe, $event)"
                                >
                                    <!-- Les valeurs appartiennent au graphe
                                     qu'elles chiffrent : elles vivent DANS sa
                                     carte. UN SEUL dépliant, qui porte toutes
                                     les séries tracées et laisse choisir
                                     laquelle on lit (lot B2). Les séries que
                                     le compteur ne publie pas en sont exclues :
                                     il n'y a rien à en chiffrer. Celles qui
                                     n'ont AUCUNE valeur sur tout le contexte
                                     chargé aussi (correctif 5a-2) : leur
                                     tableau ne serait que des cellules vides.
                                     Le tableau chiffre le CONTEXTE, pas la
                                     fenêtre regardée — le filtre porte donc sur
                                     le contexte, et une série vide seulement
                                     sur la fenêtre resserrée, qui sort de la
                                     figure, reste choisissable ici : ses
                                     chiffres existent, ailleurs dans le
                                     contexte. -->
                                    <!-- LE DÉPLIANT CHIFFRE L'ONGLET, pas la
                                         carte de détail : celle-ci porte sept
                                         jours à l'heure, et son tableau
                                         répéterait au grain de l'onglet des
                                         lignes qui ne sont pas les siennes. -->
                                    <TableauValeursEnedis
                                        v-if="!estLaCarteDeDetail(graphe)"
                                        :series="
                                            graphe.series.filter(
                                                (s) =>
                                                    !s.nonPubliee &&
                                                    !estVideSurLaFenetre(
                                                        s,
                                                        null
                                                    )
                                            )
                                        "
                                        :grain="grainTableau"
                                        :grain-servi="grainServi"
                                        :miroir="graphe.miroir === true"
                                        :jours-du-contexte="
                                            graphe.miroir
                                                ? joursDeLaBandeDuSens(
                                                      'soutirage'
                                                  )
                                                : (bandeDeCompletude?.jours ??
                                                  [])
                                        "
                                        :jours-du-contexte-injection="
                                            joursDeLaBandeDuSens('injection')
                                        "
                                        :heures-atteinte-par-serie="
                                            heuresAtteinteDesSeries
                                        "
                                        :minutes-pas-natif="minutesPasCompteur"
                                        :sommable="
                                            estSommable(ongletCourant.cle)
                                        "
                                        :unite="graphe.unite"
                                        :mentions-par-serie="
                                            mentionsDuGraphe(graphe)
                                        "
                                        @deplie="
                                            surValeursDepliees(
                                                ongletCourant.cle,
                                                $event
                                            )
                                        "
                                    />
                                </GraphiqueSeriesEnedis>
                            </div>

                            <!-- L'attente d'un onglet qui n'a encore rien
                                 chargé : la carte du graphe, son squelette, et
                                 les séries que l'écran attend. Aucun mot
                                 d'état : la carte dit qu'elle travaille. -->
                            <PlotlyChartCard
                                v-if="graphes.length === 0 && enCours"
                                :title="ongletCourant.libelle"
                                :loading="true"
                                :content-min-height="hauteurMinimaleDuContenu"
                            >
                                <template #footer>
                                    <ul
                                        class="m-0 flex list-none flex-col gap-1.5 p-0 text-sm text-color"
                                        data-testid="series-annoncees"
                                    >
                                        <li
                                            v-for="serie in ongletCourant.series"
                                            :key="serie.cle"
                                            :data-serie="serie.cle"
                                            class="flex items-center gap-2"
                                        >
                                            <span
                                                class="source-data-enedis__point source-data-enedis__point--attente h-2.5 w-2.5 rounded-full"
                                                aria-hidden="true"
                                            ></span>
                                            {{ serie.libelle }}
                                        </li>
                                    </ul>
                                </template>
                            </PlotlyChartCard>

                            <!-- Les séries de l'onglet, avec leur origine, tant
                                 qu'aucune donnée n'est encore chargée :
                                 l'écran dit ce qu'il attend. -->
                            <ul
                                v-else-if="graphes.length === 0"
                                class="m-0 flex list-none flex-col gap-1.5 p-0 text-sm text-color"
                                data-testid="series-annoncees"
                            >
                                <li
                                    v-for="serie in ongletCourant.series"
                                    :key="serie.cle"
                                    :data-serie="serie.cle"
                                    class="flex items-center gap-2"
                                >
                                    <span
                                        class="source-data-enedis__point source-data-enedis__point--attente h-2.5 w-2.5 rounded-full"
                                        aria-hidden="true"
                                    ></span>
                                    {{ serie.libelle }}
                                </li>
                            </ul>
                        </template>
                    </BaseTabPanel>
                </BaseTabPanels>
            </BaseTabs>
        </div>
    </section>
</template>

<style scoped>
/* CES TROIS CLASSES SURVIVENT COMME SÉLECTEURS AUTANT QUE COMME STYLE.
   `SourceDataEnedisView.miseEnForme.spec.ts` lignes 95-117 lit
   `.source-data-enedis__point--a-jour` et `--retard` pour prouver qu'un retard
   ne se peint pas comme une fraîcheur tenue. Tout le reste du style maison est
   parti dans les composants du système (items 12 à 18, 27, 29, 38) : ce qui
   reste ici est le strict nécessaire, et rien d'autre. */
.source-data-enedis__point--a-jour {
    background: #22c55e;
}

.source-data-enedis__point--retard {
    background: #f59e0b;
}

.source-data-enedis__point--attente {
    background: #cbd5e1;
}

/* LE VIOLET DIT « C'EST À NOUS D'AGIR » (DEV-1845 tranche 2) : un flux en retard
   parce que NOTRE collecte n'est pas passée ne se peint pas comme un flux que le
   distributeur n'a pas déposé. La couleur ne porte aucune information seule :
   la ligne écrit déjà la cause en toutes lettres à côté. */
.source-data-enedis__point--collecte {
    background: #8b5cf6;
}
</style>
